import { LoadingButton, Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from "@mui/lab";
import { Alert, AppBar, Button, Card, CardActions, CardContent, CardHeader, Chip, Dialog, FormControl, InputAdornment, InputLabel, MenuItem, OutlinedInput, Paper, Popover, Select, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { Printer } from "@untitled-ui/icons-react";
import { BarChartSquarePlus, Check, ChevronRight, Pencil01, PiggyBank01, Route, Trash01, X } from "@untitled-ui/icons-react/build/esm";
import XIcon from "@untitled-ui/icons-react/build/esm/X";
import { useFormik } from "formik";
import { pick } from "lodash";
import { useRouter } from "next/router";
import { Children, FC, Fragment, isValidElement, MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useBoolean } from "usehooks-ts";
import * as Yup from "yup";
import { CustomFieldRendererV2, CustomFieldsDialog } from "~/components/custom-fields-dialog";
import { useAppRouter } from "~/hooks/use-app-router";
import { useTillSummary } from "~/hooks/use-till-summary";
import { $dayjs, DATESTRING_FORMAT, DATETIMESTRING_FORMAT } from "~/libs/dayjs";
import { useGetAgents } from "~/queries/useGetAgents";
import { useGetClients } from "~/queries/useGetClients";
import { useGetCounters } from "~/queries/useGetCounters";
import { GetCurrentCompanyResults, useGetCurrentCompany, useGetCurrentCompanyCache } from "~/queries/useGetCurrentCompany";
import { useGetFileCustomFields } from "~/queries/useGetCustomFields";
import { GetFileByIdResults, useGetCurrentFile } from "~/queries/useGetFileById";
import { useGetProcessesCache } from "~/queries/useGetProcesses";
import { useGetProductOptions } from "~/queries/useGetProductOptions";
import { useGetProducts } from "~/queries/useGetProducts";
import { useGetUsers } from "~/queries/useGetUsers";
import { usePayForFile } from "~/queries/usePayForFile";
import { useUpdateFile } from "~/queries/useUpdateFile";
import { useResetTill, useSetTillIsOpen, useTill, useUpdateTillFileInputSnapshot, useUpdateTillUpdateFileInput } from "~/store";
import { CUSTOM_FIELD_MODEL, CUSTOM_FIELD_TYPE, DISPLAY_PREFERENCE_SECTION_DISPLAY, CustomFieldValueInput, Maybe, PayForFileMutationVariables, PAYMENT_VERIFICATION_STATUS, PAYMENT_VERIFICATION_UPLOAD_STAGE, PaymentVerification, ReceiptPaymentPayment, ROLE } from "~/types/__generated/gql/graphql";
import { getFileCustomFieldDisplayId } from "~/contexts/data-display-context/data-display-context";
import { TillFileInput } from "~/types/store";
import { DefineProcessPathsDialog } from "./define-process-paths-dialog";
import { FileStatus } from "./file-status";
import { InvoicePdfDialog } from "./invoice-pdf-dialog";
import { OrderSheetDialog } from "./order-sheet-dialog";
import { useMeCache } from "~/queries/useMe";
import { COUNTER_TYPE, DEFAULT_CURRENCY_CODE, IMAGE_FILE_EXTENSIONS, UPLOAD_PAYMENT_ATTACHMENT_MODE } from "~served/configs";
import { DateTimePicker } from "@mui/x-date-pickers";
import { bigMath, calculateFile } from "~served/utils";
import { useCancelFileById } from "~/queries/useCancelFileById";
import { DeleteFileDialog } from "./delete-file-dialog";
import { usePermissionFilters } from "~/hooks/use-permission-filters";
import { useUpdateReceiptPayment } from "~/queries/useUpdateReceiptPayment";
import CommentSection from "~/components/comment-section";
import { FileInputLike, FileProductInputLike, FileProductOptionInputLike } from "~served/utils/calculations/types";
import { Clear, Comment, Download } from "@mui/icons-material";
import PdfIcon01 from "~/icons/untitled-ui/duocolor/pdf-icon-01";
import { useUpdatePaymentVerification } from "~/queries/useUpdatePaymentVerification";
import { useGetFileDownloadUrl } from "~/queries/useGetFileDownloadUrl";
import toast from "react-hot-toast";
import { usePopover } from "~/hooks/use-popover";
import { useDeletePaymentVerification } from "~/queries/useDeletePaymentVerification";
import { $toOrderDisplay } from "~/utils/multi-currency";
import { OrderDetailTable, PaymentRecord, PaymentVerificationList } from "~/components/file";
import { $d } from "~/utils/currency-display";
import { UploadPaymentAttachmentButton } from "~/components/upload-payment-attachment/upload-payment-attachment-button";
import { CustomPaperSection } from "~/components/custom-paper-section";
import { TextFieldInput } from "~/components/form-input/text-field-input";
import { AutocompleteInput } from "~/components/form-input/autocomplete-input";
import { SelectInput } from "~/components/form-input/select-input";
import { DateTimePickerInput } from "~/components/form-input/datetime-picker-input";
import { useDataDisplay } from "~/hooks/use-data-display";
import { FileDisplaySettingsButton } from "~/layouts/dashboard/file-display-settings-button";
import { sortBySortingIndex } from "~/utils/sorting-array-result";
import { FileProductsTableSummary, TableItem, TableItemDisplay, TableSubtotalAddons } from "~/components/file-products-table-summary";
const PAYMENT_VERIFICATION_UPLOAD_STAGE_MAP = {
  [PAYMENT_VERIFICATION_UPLOAD_STAGE.DEPOSIT_PAYMENT]: "Deposit",
  [PAYMENT_VERIFICATION_UPLOAD_STAGE.PARTIAL_PAYMENT]: "Partial",
  [PAYMENT_VERIFICATION_UPLOAD_STAGE.FINAL_PAYMENT]: "Final"
};
const useTableDragScroll = () => {
  const tableRef = useRef<HTMLDivElement>(null);
  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (!tableRef.current) return;
    isDown.current = true;
    startX.current = event.pageX - tableRef.current.offsetLeft;
    scrollLeft.current = tableRef.current.scrollLeft;
  };
  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (!isDown.current || !tableRef.current) return;
    event.preventDefault();
    const x = event.pageX - tableRef.current.offsetLeft;
    tableRef.current.scrollLeft = scrollLeft.current - (x - startX.current);
  };
  const handleMouseUp = () => {
    isDown.current = false;
  };
  return {
    tableRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp
  };
};
export const ViewFileModal = () => {
  const productTableDragScroll = useTableDragScroll();
  const paymentAttachmentTableDragScroll = useTableDragScroll();
  const {
    currentActions: {
      isViewingFile
    },
    router
  } = useAppRouter();
  const {
    data: company
  } = useGetCurrentCompanyCache();
  const {
    data: me
  } = useMeCache();
  const {
    file: fileDisplay
  } = useDataDisplay();
  const {
    data: processes = []
  } = useGetProcessesCache();
  const {
    data: file,
    refetch: refetchFile,
    isLoading: isFileLoading
  } = useGetCurrentFile();
  const till = useTill();
  const setTillIsOpen = useSetTillIsOpen();
  const resetTill = useResetTill();
  const updateUpdateFileInput = useUpdateTillUpdateFileInput();
  const updateFileInputSnapshot = useUpdateTillFileInputSnapshot();
  const {
    data: agents = [],
    isLoading: isAgentsLoading
  } = useGetAgents();
  const {
    data: clients = [],
    isLoading: isClientsLoading
  } = useGetClients();
  const {
    data: users = [],
    isLoading: isUsersLoading
  } = useGetUsers();
  const {
    data: counters = [],
    isLoading: isCountersLoading
  } = useGetCounters();
  const {
    updateFileInputSummary,
    tillItemsSummary
  } = useTillSummary();
  const updateFile = useUpdateFile();
  const cancelFileById = useCancelFileById();
  const getFileDownloadUrl = useGetFileDownloadUrl();
  const {
    value: isProcessPathsDialogOpen,
    setTrue: openProcessPathsDialog,
    setFalse: closeProcessPathsDialog
  } = useBoolean(false);
  const {
    value: isFileMoving,
    toggle: toggleFileMoving
  } = useBoolean(false);
  const {
    value: isFileProcessing,
    toggle: toggleFileProcessing
  } = useBoolean(false);
  const {
    value: isCustomFieldsDialogOpen,
    setTrue: openCustomFieldsDialog,
    setFalse: closeCustomFieldsDialog
  } = useBoolean(false);
  const {
    data: customFields = []
  } = useGetFileCustomFields();
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, CustomFieldValueInput>>({});
  const [paymentVerification, setPaymentVerification] = useState<PaymentVerification["attachment"] | undefined>();
  const nextProcessId = useMemo(() => {
    if (!file?.processPaths.length) return;
    const currentProcessIndex = file.processPaths.findIndex(path => path._process._id === file._process._id);
    return file.processPaths[currentProcessIndex + 1]?._process?._id;
  }, [file]);
  const processOptions = useMemo(() => processes.map(column => ({
    label: column.name,
    value: column._id
  })), [processes]);
  const isFileFormFieldVisible = (id: string) => fileDisplay.form.sections.flatMap(section => section.rows).flatMap(row => row.fields).find(field => field.id === id)?.isVisible ?? true;
  const isFileFormSectionVisible = (name: string) => fileDisplay.form.sections.find(section => section.name === name)?.rows.flatMap(row => row.fields).some(field => field.isVisible) ?? true;
  const getProductTableVisibility = () => ({
    number: isFileFormFieldVisible("product-info-number"),
    quantity: isFileFormFieldVisible("product-info-quantity"),
    description: isFileFormFieldVisible("product-info-description"),
    total: isFileFormFieldVisible("product-info-total")
  });
  const onClose = () => {
    const newQuery = router.query;
    delete newQuery.fileId;
    resetTill();
    return router.push({
      query: newQuery
    });
  };
  const handleCancelFileById = async (id: string): Promise<void> => {
    try {
      await cancelFileById.mutateAsync({
        id: id
      });
      await onClose();
    } catch (_) {
      await onClose();
    }
  };
  const handleMoveFileToColumn = async (columnId: string, closeView: boolean): Promise<void> => {
    try {
      toggleFileMoving();
      await updateFile.mutateAsync({
        id: file!._id,
        input: {
          process: columnId
        }
      });
      toggleFileMoving();
      if (closeView) await onClose();
    } catch (_) {
      toggleFileMoving();
    }
  };
  const handleFileProcessedStatusChanged = async (): Promise<void> => {
    try {
      toggleFileProcessing();
      await updateFile.mutateAsync({
        id: file!._id,
        input: {
          isProcessed: !file!.isProcessed
        }
      });
      toggleFileProcessing();
    } catch (_) {
      toggleFileProcessing();
    }
  };
  useEffect(() => {
    if (isViewingFile && file) {
      const input: TillFileInput = {
        // skip null check to allow for unassigning agent
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        agent: file._agent?._id ?? null,
        client: file._client?._id ?? null,
        isProcessed: file.isProcessed,
        process: file._process._id,
        products: file.products.map(product => ({
          ...pick(product, ["_id", "product", "quantity", "noVat", "originalPrice", "originalPriceAddons", "listedPrice", "type", "category", "subtotalAddons", "multiCurrencyConfig", "createdAt", "updatedAt"]),
          options: product.options.map(option => pick(option, ["_id", "option", "quantity", "originalPrice", "originalPriceAddons", "listedPrice", "subtotalAddons", "multiCurrencyConfig"]))
        })) as TillFileInput["products"],
        assignees: file._assignees.map(assignee => assignee._id),
        processPaths: file.processPaths.map(processPath => ({
          process: processPath._process._id,
          leadTime: processPath.leadTime
        })),
        counterConfigs: file.counterConfigs,
        cancelledProducts: file.cancelledProducts.map(product => ({
          ...pick(product, ["_id", "product", "quantity", "noVat", "originalPrice", "originalPriceAddons", "listedPrice", "type", "category", "subtotalAddons", "multiCurrencyConfig", "createdAt", "updatedAt"]),
          options: product.options.map(option => pick(option, ["_id", "option", "quantity", "originalPrice", "originalPriceAddons", "listedPrice", "subtotalAddons", "multiCurrencyConfig"]))
        })) as TillFileInput["cancelledProducts"],
        multiCurrencyConfig: file.multiCurrencyConfig,
        customFields: file.customFields,
        fileSuffixComment: file.fileSuffixComment,
        receiptSuffixComment: file.receiptSuffixComment,
        issueDate: file.issueDate,
        dueDate: file.dueDate,
        isCancelled: file.isCancelled
      };
      updateUpdateFileInput(input);
      updateFileInputSnapshot(input);
      if (file.customFields) {
        setCustomFieldValues(file.customFields.reduce((acc, field) => {
          return {
            ...acc,
            [field.customField]: {
              customField: field.customField,
              dateTimeValue: field.dateTimeValue,
              selectValues: field.selectValues,
              textValue: field.textValue
            }
          };
        }, {}));
      }
      if (router.query.openOrderSheet) openOrderSheetDialog();
      if (router.query.takePaymentFor) {
        const takePaymentFor = router.query.takePaymentFor;
        if (!takePaymentFor) return;
        const a = (file._paymentVerifications ?? []).find(p => p._id === takePaymentFor);
        if (!a) return;
        setCurrentPaymentVerification(a as unknown as PaymentVerification);
        openPaymentDialogWithPaymentVerification();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewingFile, file]);
  const [currentPaymentVerification, setCurrentPaymentVerification] = useState<PaymentVerification | undefined>();
  const {
    value: isPaymentDialogOpen,
    setTrue: openPaymentDialog,
    setFalse: closePaymentDialog
  } = useBoolean(false);
  const {
    value: isInvoiceDialogOpen,
    setTrue: openInvoiceDialog,
    setFalse: closeInvoiceDialog
  } = useBoolean(false);
  const {
    value: isOrderSheetDialogOpen,
    setTrue: openOrderSheetDialog,
    setFalse: closeOrderSheetDialog
  } = useBoolean(false);
  const {
    value: isDeleteFileDialogOpen,
    setTrue: openDeleteFileDialogOpen,
    setFalse: closeDeleteFileDialogOpen
  } = useBoolean(false);
  const {
    value: isViewPaymentVerificationDialogDialogOpen,
    setTrue: openViewPaymentVerificationDialogDialog,
    setFalse: closeViewPaymentVerificationDialogDialog
  } = useBoolean(false);
  const {
    value: isPaymentDialogWithPaymentVerification,
    setTrue: openPaymentDialogWithPaymentVerification,
    setFalse: closePaymentDialogWithPaymentVerification
  } = useBoolean(false);
  const {
    value: isPaymentDialogWithPaymentVerificationViewMode,
    setTrue: openPaymentDialogWithPaymentVerificationViewMode,
    setFalse: closePaymentDialogWithPaymentVerificationViewMode
  } = useBoolean(false);
  const handleClosePaymentDialogWithPaymentVerification = async () => {
    await refetchFile();
    setCurrentPaymentVerification(undefined);
    closePaymentDialogWithPaymentVerification();
    closePaymentDialogWithPaymentVerificationViewMode();
    const newQuery = router.query;
    delete newQuery.takePaymentFor;
    await router.push({
      query: newQuery
    });
  };
  const {
    data: products = []
  } = useGetProducts();
  const {
    data: productOptions = []
  } = useGetProductOptions();
  const productsTableItems = useMemo<TableItem[]>(() => updateFileInputSummary.products.map(product => ({
    id: product._id.toString(),
    name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
    quantity: product.quantity,
    subtotal: product.subtotal,
    options: product.options.map(option => ({
      id: option._id.toString(),
      name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
      quantity: option.quantity,
      subtotal: option.subtotal
    })),
    subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
  })), [updateFileInputSummary.products, productOptions, products]);
  const fileItemsDisplay = useMemo<TableItemDisplay>(() => ({
    prev: tillItemsSummary.prev.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons,
      createdAt: product.createdAt,
      updatedAt: product.updatedAt
    })),
    add: tillItemsSummary.add.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
    })),
    remove: tillItemsSummary.remove.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
    }))
  }), [productOptions, products, tillItemsSummary.add.products, tillItemsSummary.prev.products, tillItemsSummary.remove.products]);
  const isAdjustmentNeeded = useMemo(() => {
    if (!file?._receipt) return false;
    const allPayments = {
      payments: file._receipt.payments.flatMap(p => p.payments.flatMap(p1 => ({
        payment_type: p1.payment_type.toString(),
        amount: p1.amount
      })))
    };
    const {
      grossAmount
    } = calculateFile({
      ...(file as unknown as FileInputLike),
      products: file.products as unknown as FileProductInputLike[],
      cancelledProducts: file.cancelledProducts as unknown as FileProductInputLike[]
    });
    const paymentAmount = allPayments.payments.reduce((acc, cur) => acc + cur.amount, 0);
    if (paymentAmount > grossAmount) return true;
    if (file._receipt.isFullyPaid && paymentAmount < grossAmount) return true;
    return false;
  }, [file]);
  const handleClosePaymentDialog = async () => {
    await refetchFile();
    void closePaymentDialog();
  };
  const handleOpenViewPaymentVerificationDialog = (attachment: PaymentVerification["attachment"]) => {
    if (!attachment) return;
    setPaymentVerification(attachment);
    openViewPaymentVerificationDialogDialog();
  };
  const handleCloseViewPaymentVerificationDialog = () => {
    setPaymentVerification(undefined);
    closeViewPaymentVerificationDialogDialog();
  };
  const {
    value: updateFileCommentDialog,
    setTrue: openUpdateFileCommentDialog,
    setFalse: closeUpdateFileCommentDialog
  } = useBoolean(false);
  const UpdateOrderNumberComment: FC = () => {
    const [state, setState] = useState<string | null | undefined>();
    useEffect(() => {
      if (file?.fileSuffixComment) setState(file?.fileSuffixComment);
    }, []);
    return <Dialog open maxWidth="sm" data-sentry-element="Dialog" data-sentry-component="UpdateOrderNumberComment" data-sentry-source-file="view-file-modal.tsx">
				<Stack spacing={3} sx={{
        p: 3
      }} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
					<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
						Update {company?.fileAlias.singular ?? "File"} Reference Number
						Comment
					</Typography>
					<Stack direction="row" spacing={3} justifyContent="flex-end" data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
						<TextFieldInput id="file-number-comment" label="New option" value={state} onChange={e => setState(e.target.value)} data-sentry-element="TextFieldInput" data-sentry-source-file="view-file-modal.tsx" />
					</Stack>
					<Stack direction="row" spacing={3} justifyContent="flex-end" data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
						<Button color="inherit" onClick={closeUpdateFileCommentDialog} data-sentry-element="Button" data-sentry-source-file="view-file-modal.tsx">
							Cancel
						</Button>
						<Button variant="contained" onClick={async () => {
            const comment = state ?? "";
            await updateFile.mutateAsync({
              id: file!._id,
              input: {
                fileSuffixComment: comment
              }
            });
            updateUpdateFileInput({
              fileSuffixComment: comment
            });
            void closeUpdateFileCommentDialog();
          }} data-sentry-element="Button" data-sentry-source-file="view-file-modal.tsx">
							Update
						</Button>
					</Stack>
				</Stack>
			</Dialog>;
  };
  const {
    value: updateInvoiceCommentDialog,
    setTrue: openUpdateInvoiceCommentDialog,
    setFalse: closeUpdateInvoiceCommentDialog
  } = useBoolean(false);
  const UpdateInvoiceNumberComment: FC = () => {
    const [state, setState] = useState<string | null | undefined>();
    useEffect(() => {
      if (file?.receiptSuffixComment) setState(file?.receiptSuffixComment);
    }, []);
    return <Dialog open maxWidth="sm" data-sentry-element="Dialog" data-sentry-component="UpdateInvoiceNumberComment" data-sentry-source-file="view-file-modal.tsx">
				<Stack spacing={3} sx={{
        p: 3
      }} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
					<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
						Update Invoice Reference Number Comment
					</Typography>
					<Stack direction="row" spacing={3} justifyContent="flex-end" data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
						<TextFieldInput id="file-number-comment" label="New option" value={state} onChange={e => setState(e.target.value)} data-sentry-element="TextFieldInput" data-sentry-source-file="view-file-modal.tsx" />
					</Stack>
					<Stack direction="row" spacing={3} justifyContent="flex-end" data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
						<Button color="inherit" onClick={closeUpdateInvoiceCommentDialog} data-sentry-element="Button" data-sentry-source-file="view-file-modal.tsx">
							Cancel
						</Button>
						<Button variant="contained" onClick={async () => {
            const comment = state ?? "";
            await updateFile.mutateAsync({
              id: file!._id,
              input: {
                receiptSuffixComment: comment
              }
            });
            updateUpdateFileInput({
              receiptSuffixComment: comment
            });
            void closeUpdateInvoiceCommentDialog();
          }} data-sentry-element="Button" data-sentry-source-file="view-file-modal.tsx">
							Update
						</Button>
					</Stack>
				</Stack>
			</Dialog>;
  };
  const OrderFormGeneralInfoSection: FC = () => {
    // if (!isFileFormSectionVisible("General Information")) return null;
    const generalInfoSection = fileDisplay.form.sections.find(section => section.name === "General Information");
    if (!generalInfoSection) return null;
    const rows = (generalInfoSection?.rows.length ? generalInfoSection.rows : [{
      sortingIndex: 0,
      fields: [{
        sortingIndex: 0,
        id: "general-info-created-by",
        isVisible: true
      }, {
        sortingIndex: 1,
        id: "general-info-created-at",
        isVisible: true
      }, {
        sortingIndex: 2,
        id: "general-info-last-updated",
        isVisible: true
      }]
    }]).sort(sortBySortingIndex);
    return <CustomPaperSection key="order-form-general-info-section" title="General Information" data-sentry-element="CustomPaperSection" data-sentry-component="OrderFormGeneralInfoSection" data-sentry-source-file="view-file-modal.tsx">
				{rows.map((row, rowIndex) => {
        const fields = row.fields.filter(field => field.isVisible).sort(sortBySortingIndex);
        const gridSize = bigMath.div(12, fields.length);
        switch (generalInfoSection.display) {
          case DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID:
            return <Fragment key={`order-form-general-info-row-${rowIndex}`}>
									{fields.map((field, fieldIndex) => {
                const formField = (() => {
                  switch (field.id) {
                    case "general-info-created-by":
                      return <TextFieldInput id="om-created-by" label="Created By" value={file?._user ? `${file._user.lastName} ${file._user.firstName}` : "Unknown User"} disabled />;
                    case "general-info-created-at":
                      return <TextFieldInput id="om-created-at" label="Created At" value={$dayjs(file?.createdAt).format(DATETIMESTRING_FORMAT)} disabled />;
                    case "general-info-last-updated":
                      return <TextFieldInput id="om-last-updated" label="Last Updated" value={updateFile.isLoading ? "Saving..." : $dayjs(file?.updatedAt).format(DATETIMESTRING_FORMAT)} disabled />;
                    default:
                      return null;
                  }
                })();
                if (!formField) return null;
                return <Grid key={`form-field-general-info-${field.id}-${fieldIndex}`} xs={bigMath.mul(gridSize, 2)} md={gridSize}>
												{formField}
											</Grid>;
              })}
								</Fragment>;
          default:
            return null;
        }
      })}
			</CustomPaperSection>;
  };
  const OrderFormBasicInfoSection: FC = () => {
    if (!isFileFormSectionVisible("Basic Information")) return null;
    const basicInfoSection = fileDisplay.form.sections.find(section => section.name === "Basic Information");
    const basicInfoRows = (basicInfoSection?.rows ?? []).slice().sort(sortBySortingIndex);
    const basicInfoFields = basicInfoRows.flatMap(row => row.fields.filter(field => field.isVisible).slice().sort(sortBySortingIndex));
    const isBasicInfoFieldVisible = (id: string) => basicInfoRows.flatMap(row => row.fields).find(field => field.id === id)?.isVisible ?? true;
    const getBasicInfoGridSize = (id: string) => {
      const row = basicInfoRows.find(item => item.fields.some(field => field.id === id));
      const visibleFieldCount = Math.max(row?.fields.filter(field => field.isVisible).length ?? 0, 1);
      return bigMath.div(12, visibleFieldCount);
    };
    const getBasicInfoFieldOrder = (id: string) => {
      const index = basicInfoFields.findIndex(field => field.id === id);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };
    return <CustomPaperSection key="order-form-basic-info-info-section" title="Basic Information" data-sentry-element="CustomPaperSection" data-sentry-component="OrderFormBasicInfoSection" data-sentry-source-file="view-file-modal.tsx">
				<Fragment key={`order-form-basic-info-info-section-fragment-0`}>
					<Grid key="order-form-basic-info-info-section-grid-0" sx={{
          display: isBasicInfoFieldVisible("basic-info-client") ? undefined : "none",
          order: getBasicInfoFieldOrder("basic-info-client")
        }}
        // item
        xs={bigMath.mul(getBasicInfoGridSize("basic-info-client"), 2)} md={getBasicInfoGridSize("basic-info-client")} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<AutocompleteInput id="om-select-client" label="Select Client" fullWidth options={clients} renderOption={(props, option, {
            inputValue
          }) => {
            const label = `${option.companyIssuedId}-${option.name}`;
            const matches = label.toLowerCase().indexOf(inputValue.toLowerCase());
            const parts = matches !== -1 ? [label.substring(0, matches), label.substring(matches, matches + inputValue.length), label.substring(matches + inputValue.length)] : [label];
            return <li {...props} key={option._id} style={{
              whiteSpace: "pre-wrap"
            }}>
										{parts.map((part, index) => <Box key={index} component="span" sx={part.toLowerCase() === inputValue.toLowerCase() ? {
                fontWeight: 800,
                bgcolor: "primary.main"
              } : {}}>
												{part}
											</Box>)}
									</li>;
          }} value={clients.find(client => client._id === till.updateFileInput?.client) ?? null} isOptionEqualToValue={(option, value) => option._id === value?._id} getOptionLabel={option => `${option.companyIssuedId}-${option.name}`} filterOptions={(options, {
            inputValue
          }) => {
            const input = inputValue.toLowerCase();
            const matchOptions = options.filter(o => {
              const label = `${o.companyIssuedId}-${o.name}`;
              const customFields = o.customFields.map(c => c.textValue).join(":");
              const contacts = (o.contacts ?? []).map(contact => `${contact.email}:${contact.firstName}:${contact.lastName}:${contact.mobile}:${contact.telephone}:${contact.customFields.filter(custom => custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_SINGLE || custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_MULTI).map(c => c.textValue).join(":")}`);
              const matchLabel = label.toLowerCase().includes(input);
              const matchSearch = `${customFields}:${contacts}`.toLowerCase().includes(input);
              return matchLabel || matchSearch;
            });
            const matches = matchOptions.filter(option => {
              const label = `${option.companyIssuedId}-${option.name}`;
              return label.toLowerCase().includes(input.toLowerCase());
            });
            const nonMatches = matchOptions.filter(option => {
              const label = `${option.companyIssuedId}-${option.name}`;
              return !label.toLowerCase().includes(input.toLowerCase());
            });
            return [...matches, ...nonMatches];
          }} onChange={async (_, value, reason) => {
            if (reason !== "clear") {
              await updateFile.mutateAsync({
                id: file!._id,
                input: {
                  client: value?._id
                }
              });
            }
            updateUpdateFileInput({
              client: value?._id
            });
          }} onInput={e => {
            const event = e.nativeEvent as InputEvent;
            const input = e.target as any;
            if (event.inputType === "insertFromPaste") input.value = input.value.trim();
          }} onBlur={_e => {
            if (file?._client) {
              updateUpdateFileInput({
                client: file._client?._id
              });
            }
          }} renderInput={params => <TextField {...params} label="Clients" />} data-sentry-element="AutocompleteInput" data-sentry-source-file="view-file-modal.tsx" />
					</Grid>
					<Grid key="order-form-basic-info-info-section-grid-1" sx={{
          display: isBasicInfoFieldVisible("basic-info-agents") ? undefined : "none",
          order: getBasicInfoFieldOrder("basic-info-agents")
        }}
        // item
        xs={bigMath.mul(getBasicInfoGridSize("basic-info-agents"), 2)} md={getBasicInfoGridSize("basic-info-agents")} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<AutocompleteInput id="om-select-agents" label="Select Agents" fullWidth options={agents} renderOption={(props, option, {
            inputValue
          }) => {
            const label = `${option.companyIssuedId}-${option.name}`;
            const matches = label.toLowerCase().indexOf(inputValue.toLowerCase());
            const parts = matches !== -1 ? [label.substring(0, matches), label.substring(matches, matches + inputValue.length), label.substring(matches + inputValue.length)] : [label];
            return <li {...props} key={option._id} style={{
              whiteSpace: "pre-wrap"
            }}>
										{parts.map((part, index) => <Box key={index} component="span" sx={part.toLowerCase() === inputValue.toLowerCase() ? {
                fontWeight: 800,
                bgcolor: "primary.main"
              } : {}}>
												{part}
											</Box>)}
									</li>;
          }} value={agents.find(agent => agent._id === till.updateFileInput?.agent) ?? null} isOptionEqualToValue={(option, value) => option._id === value?._id} getOptionLabel={option => `${option.companyIssuedId}-${option.name}`} filterOptions={(options, {
            inputValue
          }) => {
            const input = inputValue.toLowerCase();
            const matchOptions = options.filter(o => {
              const label = `${o.companyIssuedId}-${o.name}`;
              const customFields = o.customFields.map(c => c.textValue).join(":");
              const contacts = (o.contacts ?? []).map(contact => `${contact.email}:${contact.firstName}:${contact.lastName}:${contact.mobile}:${contact.telephone}:${contact.customFields.filter(custom => custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_SINGLE || custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_MULTI).map(c => c.textValue).join(":")}`);
              const matchLabel = label.toLowerCase().includes(input);
              const matchSearch = `${customFields}:${contacts}`.toLowerCase().includes(input);
              return matchLabel || matchSearch;
            });
            const matches = matchOptions.filter(option => {
              const label = `${option.companyIssuedId}-${option.name}`;
              return label.toLowerCase().includes(input.toLowerCase());
            });
            const nonMatches = matchOptions.filter(option => {
              const label = `${option.companyIssuedId}-${option.name}`;
              return !label.toLowerCase().includes(input.toLowerCase());
            });
            return [...matches, ...nonMatches];
          }} onChange={async (_, value, reason) => {
            if (reason !== "clear") {
              await updateFile.mutateAsync({
                id: file!._id,
                input: {
                  agent: value?._id
                }
              });
            }
            updateUpdateFileInput({
              agent: value?._id
            });
          }} onInput={e => {
            const event = e.nativeEvent as InputEvent;
            const input = e.target as any;
            if (event.inputType === "insertFromPaste") input.value = input.value.trim();
          }} onBlur={_e => {
            if (file?._agent) {
              updateUpdateFileInput({
                agent: file._agent?._id
              });
            }
          }} renderInput={params => <TextField {...params} label="Agents" />} data-sentry-element="AutocompleteInput" data-sentry-source-file="view-file-modal.tsx" />
					</Grid>
					<Grid key="order-form-basic-info-info-section-grid-2" sx={{
          display: isBasicInfoFieldVisible("basic-info-assignees") ? undefined : "none",
          order: getBasicInfoFieldOrder("basic-info-assignees")
        }}
        // item
        xs={bigMath.mul(getBasicInfoGridSize("basic-info-assignees"), 2)} md={getBasicInfoGridSize("basic-info-assignees")} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<AutocompleteInput id="om-select-assignees" label="Select Assignees" multiple fullWidth disableCloseOnSelect options={users} value={users.filter(user => till.updateFileInput?.assignees?.includes(user._id)) ?? null} isOptionEqualToValue={(option, value) => option._id === value._id} getOptionLabel={option => option.firstName} onChange={async (_, values) => {
            await updateFile.mutateAsync({
              id: file!._id,
              input: {
                assignees: values.map(user => user._id)
              }
            });
            updateUpdateFileInput({
              assignees: values.map(user => user._id)
            });
          }} renderInput={params => <TextField {...params} />} renderTags={(value, getTagProps) => value.map((value, index) => <Chip {...getTagProps({
            index
          })} key={value._id} label={value.firstName} avatar={<Avatar src={value.avatar ?? undefined} />} />)} renderOption={(props, option) => <li {...props}>
									<Avatar src={option.avatar ?? undefined} />
									&nbsp;{option.firstName}
								</li>} data-sentry-element="AutocompleteInput" data-sentry-source-file="view-file-modal.tsx" />
					</Grid>
					<Grid key="order-form-basic-info-info-section-grid-3" sx={{
          display: isBasicInfoFieldVisible("basic-info-file-number") ? undefined : "none",
          order: getBasicInfoFieldOrder("basic-info-file-number")
        }}
        // item
        xs={bigMath.mul(getBasicInfoGridSize("basic-info-file-number"), 2)} md={getBasicInfoGridSize("basic-info-file-number")} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<SelectInput id="om-select-order-counter-number" label={`${company?.fileAlias.singular ?? "File"} #`} value={file?.counterConfigs?.file?._counter?._id ?? ""} onChange={async e => {
            if (!e.target.value) return;
            await updateFile.mutateAsync({
              id: file!._id,
              input: {
                counterConfigs: {
                  file: {
                    counter: String(e.target.value)
                  }
                }
              }
            });
            updateUpdateFileInput({
              counterConfigs: {
                file: {
                  counter: String(e.target.value)
                }
              }
            });
          }} renderValue={selected => {
            if (file?.counterConfigs?.file) {
              return file.counterConfigs.file.reference;
            } else {
              const sel = counters.find(c => c._id === selected);
              if (!sel) return "";
              return sel.isPredefinedFormat ? `${sel.prefix ? `${sel.prefix}-` : ""}YYYYMM${file?.fileSuffixComment ? `-${file?.fileSuffixComment}` : ""}` : `${sel.prefix}-${file?.fileSuffixComment ? `-${file?.fileSuffixComment}` : ""}`;
            }
          }} input={<OutlinedInput endAdornment={<InputAdornment position="end">
											<IconButton onClick={e => {
              e.stopPropagation();
              void openUpdateFileCommentDialog();
            }} edge="end" size="small" aria-label="Update Comment" sx={{
              mr: 3
            }}>
												<Comment />
											</IconButton>
										</InputAdornment>} />} data-sentry-element="SelectInput" data-sentry-source-file="view-file-modal.tsx">
							{counters.filter(i => i.type === COUNTER_TYPE.FILE && i.isDisabled === false).map(counter => <MenuItem key={counter._id} value={counter._id}>
										{counter.isPredefinedFormat ? `${counter.prefix ? `${counter.prefix}-` : ""}YYYYMM` : counter.prefix}
									</MenuItem>)}
						</SelectInput>
					</Grid>
					{customFields.map(field => <Grid xs={bigMath.mul(getBasicInfoGridSize(getFileCustomFieldDisplayId(field._id)), 2)} md={getBasicInfoGridSize(getFileCustomFieldDisplayId(field._id))} sx={{
          display: isBasicInfoFieldVisible(getFileCustomFieldDisplayId(field._id)) ? undefined : "none",
          order: getBasicInfoFieldOrder(getFileCustomFieldDisplayId(field._id))
        }} key={`${field._id}-input`}>
							<CustomFieldRendererV2 field={field} value={customFieldValues[field._id]} onChange={async value => {
            const newCustomFields = customFieldValues;
            delete newCustomFields._customFields;
            const updatedCustomFieldValues = {
              ...newCustomFields,
              [value.customField]: value
            };
            await updateFile.mutateAsync({
              id: file!._id,
              input: {
                customFields: Object.values(updatedCustomFieldValues)
              }
            });
            setCustomFieldValues(updatedCustomFieldValues);
          }} />
						</Grid>)}

					<Grid xs={12} sm={12} sx={{
          order: 99
        }} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<Grid container data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Grid xs={6} sm={6} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
								{!!file?.processPaths?.length && <Timeline sx={{
                m: 0,
                py: 0
              }}>
										{file.processPaths.slice(0, 3).map((path, index, current) => {
                  return <TimelineItem key={index} sx={{
                    "::before": {
                      content: "unset"
                    }
                  }}>
														<TimelineSeparator>
															<TimelineDot color={path._process._id === file?._process._id ? "primary" : "grey"} />
															{index < 2 && !!current[index + 1] && <TimelineConnector />}
															{index === 2 && !!file.processPaths[3] && <TimelineConnector />}
														</TimelineSeparator>
														<TimelineContent>
															{path._process.name}
														</TimelineContent>
													</TimelineItem>;
                })}
									</Timeline>}
								<Button startIcon={<SvgIcon>
											<Route />
										</SvgIcon>} onClick={openProcessPathsDialog} data-sentry-element="Button" data-sentry-source-file="view-file-modal.tsx">
									Define process paths
								</Button>
							</Grid>
							{(me?.role === ROLE.SUPER_ADMIN || company?.isCustomFieldEnabled) && <Grid xs={6} sx={{
              display: isBasicInfoFieldVisible("basic-info-manage-custom-fields") ? undefined : "none",
              order: getBasicInfoFieldOrder("basic-info-manage-custom-fields")
            }} md={6} textAlign="right">
									<Button startIcon={<BarChartSquarePlus />} onClick={openCustomFieldsDialog}>
										Manage custom fields
									</Button>
								</Grid>}
						</Grid>
					</Grid>
				</Fragment>
			</CustomPaperSection>;
  };
  const OrderInvoiceInfoSection: FC = () => {
    if (!isFileFormSectionVisible("Invoice Information")) return null;
    const invoiceInfoSection = fileDisplay.form.sections.find(section => section.name === "Invoice Information");
    const invoiceInfoRows = (invoiceInfoSection?.rows ?? []).slice().sort(sortBySortingIndex);
    const invoiceInfoFields = invoiceInfoRows.flatMap(row => row.fields.filter(field => field.isVisible).slice().sort(sortBySortingIndex));
    const isInvoiceInfoFieldVisible = (id: string) => invoiceInfoRows.flatMap(row => row.fields).find(field => field.id === id)?.isVisible ?? true;
    const getInvoiceInfoGridSize = (id: string) => {
      const row = invoiceInfoRows.find(item => item.fields.some(field => field.id === id));
      const visibleFieldCount = Math.max(row?.fields.filter(field => field.isVisible).length ?? 0, 1);
      return bigMath.div(12, visibleFieldCount);
    };
    const getInvoiceInfoFieldOrder = (id: string) => {
      const index = invoiceInfoFields.findIndex(field => field.id === id);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };
    return <CustomPaperSection key="order-form-basic-info-info-section" title="Invoice Information" data-sentry-element="CustomPaperSection" data-sentry-component="OrderInvoiceInfoSection" data-sentry-source-file="view-file-modal.tsx">
				<Fragment key="order-form-invoice-info-fragment">
					<Grid key="order-form-invoice-info-info-section-grid-0" sx={{
          display: isInvoiceInfoFieldVisible("invoice-info-invoice-number") ? undefined : "none",
          order: getInvoiceInfoFieldOrder("invoice-info-invoice-number")
        }}
        // item
        xs={bigMath.mul(getInvoiceInfoGridSize("invoice-info-invoice-number"), 2)} md={getInvoiceInfoGridSize("invoice-info-invoice-number")} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<SelectInput id="om-select-invoice-counter-number" label={`Invoice #`} value={file?.counterConfigs?.invoice?._counter?._id ?? ""} onChange={async e => {
            if (!e.target.value) return;
            await updateFile.mutateAsync({
              id: file!._id,
              input: {
                counterConfigs: {
                  invoice: {
                    counter: String(e.target.value)
                  }
                }
              }
            });
            updateUpdateFileInput({
              counterConfigs: {
                invoice: {
                  counter: String(e.target.value)
                }
              }
            });
          }} renderValue={selected => {
            if (file?.counterConfigs?.invoice) {
              return file.counterConfigs.invoice.reference;
            } else {
              const sel = counters.find(c => c._id === selected);
              if (!sel) return "";
              return sel.isPredefinedFormat ? `${sel.prefix ? `${sel.prefix}-` : ""}YYYYMM${file?.receiptSuffixComment ? `-${file?.receiptSuffixComment}` : ""}` : `${sel.prefix}-${file?.receiptSuffixComment ? `-${file?.receiptSuffixComment}` : ""}`;
            }
          }} input={<OutlinedInput endAdornment={<InputAdornment position="end">
											<IconButton onClick={e => {
              e.stopPropagation();
              void openUpdateInvoiceCommentDialog();
            }} edge="end" size="small" aria-label="Update Comment" sx={{
              mr: 3
            }}>
												<Comment />
											</IconButton>
										</InputAdornment>} />} data-sentry-element="SelectInput" data-sentry-source-file="view-file-modal.tsx">
							{counters.filter(i => i.type === COUNTER_TYPE.INVOICE && i.isDisabled === false).map(counter => <MenuItem key={counter._id} value={counter._id}>
										{counter.isPredefinedFormat ? `${counter.prefix ? `${counter.prefix}-` : ""}YYYYMM` : counter.prefix}
									</MenuItem>)}
						</SelectInput>
					</Grid>
					<Grid key="order-form-invoice-info-info-section-grid-1" sx={{
          display: isInvoiceInfoFieldVisible("invoice-info-issue-date") ? undefined : "none",
          order: getInvoiceInfoFieldOrder("invoice-info-issue-date")
        }}
        // item
        xs={bigMath.mul(getInvoiceInfoGridSize("invoice-info-issue-date"), 2)} md={getInvoiceInfoGridSize("invoice-info-issue-date")} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<DateTimePickerInput id="" label="Issue Date" value={till.updateFileInput?.issueDate ? new Date(till.updateFileInput?.issueDate) : null} onChange={async value => {
            if (!value) return;
            await updateFile.mutateAsync({
              id: file!._id,
              input: {
                issueDate: value.toISOString()
              }
            });
            updateUpdateFileInput({
              issueDate: value.toISOString()
            });
          }} data-sentry-element="DateTimePickerInput" data-sentry-source-file="view-file-modal.tsx" />
					</Grid>
					<Grid key="order-form-invoice-info-info-section-grid-2" sx={{
          display: isInvoiceInfoFieldVisible("invoice-info-due-date") ? undefined : "none",
          order: getInvoiceInfoFieldOrder("invoice-info-due-date")
        }}
        // item
        xs={bigMath.mul(getInvoiceInfoGridSize("invoice-info-due-date"), 2)} md={getInvoiceInfoGridSize("invoice-info-due-date")} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<DateTimePickerInput sx={{
            width: "100%"
          }} label="Due Date" value={till.updateFileInput?.dueDate ? new Date(till.updateFileInput?.dueDate) : null} onChange={async value => {
            if (!value) return;
            await updateFile.mutateAsync({
              id: file!._id,
              input: {
                dueDate: value.toISOString()
              }
            });
            updateUpdateFileInput({
              dueDate: value.toISOString()
            });
          }} data-sentry-element="DateTimePickerInput" data-sentry-source-file="view-file-modal.tsx" />
					</Grid>
				</Fragment>
			</CustomPaperSection>;
  };
  const OrderProductInfoSection: FC = () => {
    if (!isFileFormSectionVisible("Product Information")) return null;
    return <CustomPaperSection key="order-product-info-info-section" title="Product Information" data-sentry-element="CustomPaperSection" data-sentry-component="OrderProductInfoSection" data-sentry-source-file="view-file-modal.tsx">
				<Fragment key={`order-product-info-info-section-fragment-0`}>
					<Grid xs={12} sx={{
          display: isFileFormFieldVisible("product-info-table") ? undefined : "none"
        }} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						{productsTableItems.length && <TableContainer component={Paper} ref={productTableDragScroll.tableRef} onMouseDown={productTableDragScroll.handleMouseDown} onMouseMove={productTableDragScroll.handleMouseMove} onMouseUp={productTableDragScroll.handleMouseUp} onMouseLeave={productTableDragScroll.handleMouseUp} sx={{
            width: "100%",
            overflowX: "auto",
            "& table": {
              width: "max-content",
              minWidth: "100%",
              border: "1.5px solid",
              borderColor: "divider",
              borderCollapse: "collapse"
            },
            "& .MuiTableCell-root": {
              borderRight: "1.5px solid",
              borderRightColor: "divider",
              borderBottom: "1.5px solid",
              borderBottomColor: "divider"
            },
            "& th:nth-of-type(3), & tbody tr td:nth-of-type(3)": {
              minWidth: 500
            },
            "& th:last-child, & td:last-child": {
              borderRight: "none"
            },
            "& tbody tr:last-child td": {
              borderBottom: "none"
            }
          }}>
								<FileProductsTableSummary items={productsTableItems} itemsDisplay={fileItemsDisplay} columnVisibility={getProductTableVisibility()} subtotal={updateFileInputSummary.subtotal} additionalFee={updateFileInputSummary.additionalFeeAmount} fixDiscount={updateFileInputSummary.fixDiscountAmount} grandTotal={updateFileInputSummary.grossAmount} isUpdateOnDiscountChange={true} isUpdateOnAdditionalFeeChange={true} />
							</TableContainer>}
					</Grid>
					<Grid xs={12} sx={{
          display: isFileFormFieldVisible("product-info-actions") ? undefined : "none"
        }} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx"></Grid>
				</Fragment>
			</CustomPaperSection>;
  };
  const OrderPaymentRecordSection: FC = () => {
    if (!isFileFormSectionVisible("Payment Records")) return null;
    return <CustomPaperSection key="order-payment-records-section" title="Payment Records" data-sentry-element="CustomPaperSection" data-sentry-component="OrderPaymentRecordSection" data-sentry-source-file="view-file-modal.tsx">
				<Fragment key={`order-payment-records-section-fragment-0`}>
					<Grid xs={12} sx={{
          display: isFileFormFieldVisible("payment-records") ? undefined : "none"
        }} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<PaymentHistoryTable file={file!} company={company!} data-sentry-element="PaymentHistoryTable" data-sentry-source-file="view-file-modal.tsx" />
					</Grid>
				</Fragment>
			</CustomPaperSection>;
  };
  const OrderPaymentVerificationSection: FC = () => {
    const paymentAttachmentSection = fileDisplay.form.sections.find(section => section.name === "Payment Attachments");
    const paymentAttachmentFieldIds = ["payment-attachment-number", "payment-attachment-amount", "payment-attachment-uploaded", "payment-attachment-attachment", "payment-attachment-payment-amount", "payment-attachment-payment-currency", "payment-attachment-exchange-rate", "payment-attachment-order-currency", "payment-attachment-order-amount", "payment-attachment-received-status", "payment-attachment-payment-stage", "payment-attachment-status", "payment-attachment-void", "payment-attachment-voided-amount", "payment-attachment-payment-action", "payment-attachment-remove-action"];
    const paymentAttachmentRows = (paymentAttachmentSection?.rows.length ? paymentAttachmentSection.rows : [{
      sortingIndex: 0,
      fields: paymentAttachmentFieldIds.map((id, sortingIndex) => ({
        sortingIndex,
        id,
        isVisible: true
      }))
    }]).slice().sort(sortBySortingIndex);
    const paymentAttachmentFields = paymentAttachmentRows.flatMap(row => row.fields.filter(field => field.isVisible).slice().sort(sortBySortingIndex));
    const paymentAttachmentFieldOrder = new Map(paymentAttachmentFields.map((field, index) => [field.id, index]));
    const sortPaymentAttachmentCells = (children: ReactNode): ReactNode[] => {
      const flattenCells = (nodes: ReactNode): ReactNode[] => Children.toArray(nodes).flatMap(node => isValidElement<{
        children?: ReactNode;
      }>(node) && node.type === Fragment ? flattenCells(node.props.children) : [node]);
      const getFieldId = (node: ReactNode) => isValidElement<{
        "data-payment-attachment-field"?: string;
      }>(node) ? node.props["data-payment-attachment-field"] : undefined;
      const getFieldOrder = (node: ReactNode) => {
        const fieldId = getFieldId(node);
        return fieldId === undefined ? Number.MAX_SAFE_INTEGER : paymentAttachmentFieldOrder.get(fieldId) ?? Number.MAX_SAFE_INTEGER;
      };
      return flattenCells(children).filter(cell => {
        const fieldId = getFieldId(cell);
        return fieldId !== undefined && paymentAttachmentFieldOrder.has(fieldId);
      }).sort((a, b) => getFieldOrder(a) - getFieldOrder(b));
    };
    if (!company?.isPaymentVerificationEnabled) return null;
    if (!isFileFormSectionVisible("Payment Attachments")) return null;
    return <Fragment key={`order-payment-payment-attachment-section-fragment-0`}>
				<Grid xs={12} sx={{
        display: isFileFormFieldVisible("payment-attachments") ? undefined : "none"
      }} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
					<TableContainer component={Paper} ref={paymentAttachmentTableDragScroll.tableRef} onMouseDown={paymentAttachmentTableDragScroll.handleMouseDown} onMouseMove={paymentAttachmentTableDragScroll.handleMouseMove} onMouseUp={paymentAttachmentTableDragScroll.handleMouseUp} onMouseLeave={paymentAttachmentTableDragScroll.handleMouseUp} sx={{
          width: "100%",
          overflowX: "auto"
        }} data-sentry-element="TableContainer" data-sentry-source-file="view-file-modal.tsx">
						<Table sx={theme => ({
            border: `1.5px solid ${theme.palette.divider}`,
            borderCollapse: "collapse",
            "& th, & td": {
              minWidth: 180,
              borderRight: `1.5px solid ${theme.palette.divider}`,
              borderBottom: `1.5px solid ${theme.palette.divider}`
            },
            "& th:last-child, & td:last-child": {
              borderRight: "none",
              borderLeft: "none"
            },
            "& tbody tr:last-child td": {
              borderBottom: "none"
            },
            '& tbody td[data-payment-attachment-field="payment-attachment-payment-action"]': {
              minWidth: 180,
              width: 180,
              maxWidth: 180
            },
            '& thead th[data-payment-attachment-field="payment-attachment-payment-action"]': {
              minWidth: 180,
              width: 180,
              maxWidth: 180
            },
            '& tbody td[data-payment-attachment-field="payment-attachment-remove-action"]': {
              minWidth: 85,
              width: 85,
              maxWidth: 85
            },
            '& thead th[data-payment-attachment-field="payment-attachment-remove-action"]': {
              minWidth: 85,
              width: 85,
              maxWidth: 85
            }
          })} data-sentry-element="Table" data-sentry-source-file="view-file-modal.tsx">
							<TableHead data-sentry-element="TableHead" data-sentry-source-file="view-file-modal.tsx">
								<TableRow data-sentry-element="TableRow" data-sentry-source-file="view-file-modal.tsx">
									{sortPaymentAttachmentCells(<>
											<TableCell data-payment-attachment-field="payment-attachment-number" sx={{
                    minWidth: 300,
                    display: isFileFormFieldVisible("payment-attachment-number") ? undefined : "none"
                  }}>
												Payment #
											</TableCell>
											<TableCell data-payment-attachment-field="payment-attachment-amount" sx={{
                    minWidth: 220,
                    display: isFileFormFieldVisible("payment-attachment-amount") ? undefined : "none"
                  }} align="right">
												Payment Attachment Amount
											</TableCell>
											<TableCell data-payment-attachment-field="payment-attachment-uploaded" sx={{
                    display: isFileFormFieldVisible("payment-attachment-uploaded") ? undefined : "none"
                  }}>
												Uploaded
											</TableCell>
											<TableCell data-payment-attachment-field="payment-attachment-attachment" sx={{
                    display: isFileFormFieldVisible("payment-attachment-attachment") ? undefined : "none"
                  }}>
												Attachment
											</TableCell>
											{company.isMultiCurrencyEnabled && <Fragment>
													<TableCell data-payment-attachment-field="payment-attachment-payment-amount" sx={{
                      minWidth: 220,
                      display: isFileFormFieldVisible("payment-attachment-payment-amount") ? undefined : "none"
                    }} align="right">
														Payment Amt
													</TableCell>
													<TableCell data-payment-attachment-field="payment-attachment-payment-currency" sx={{
                      display: isFileFormFieldVisible("payment-attachment-payment-currency") ? undefined : "none"
                    }}>
														Payment Currency
													</TableCell>
													<TableCell data-payment-attachment-field="payment-attachment-exchange-rate" sx={{
                      minWidth: 180,
                      display: isFileFormFieldVisible("payment-attachment-exchange-rate") ? undefined : "none"
                    }}>
														Exchange Rate
													</TableCell>
													<TableCell data-payment-attachment-field="payment-attachment-order-currency" sx={{
                      display: isFileFormFieldVisible("payment-attachment-order-currency") ? undefined : "none"
                    }}>
														Order Currency
													</TableCell>
												</Fragment>}
											<TableCell data-payment-attachment-field="payment-attachment-order-amount" sx={{
                    minWidth: 220,
                    display: isFileFormFieldVisible("payment-attachment-order-amount") ? undefined : "none"
                  }} align="right">
												Payment Amt in Order Currency
											</TableCell>
											<TableCell data-payment-attachment-field="payment-attachment-received-status" sx={{
                    minWidth: 180,
                    display: isFileFormFieldVisible("payment-attachment-received-status") ? undefined : "none"
                  }}>
												Received Status
											</TableCell>
											<TableCell data-payment-attachment-field="payment-attachment-payment-stage" sx={{
                    minWidth: 180,
                    display: isFileFormFieldVisible("payment-attachment-payment-stage") ? undefined : "none"
                  }}>
												Payment Stage
											</TableCell>
											<TableCell data-payment-attachment-field="payment-attachment-status" sx={{
                    minWidth: 180,
                    display: isFileFormFieldVisible("payment-attachment-status") ? undefined : "none"
                  }}>
												Finance Verification
											</TableCell>
											<TableCell data-payment-attachment-field="payment-attachment-void" sx={{
                    minWidth: 180,
                    display: isFileFormFieldVisible("payment-attachment-void") ? undefined : "none"
                  }}>
												Void
											</TableCell>
											<TableCell data-payment-attachment-field="payment-attachment-voided-amount" sx={{
                    minWidth: 220,
                    display: isFileFormFieldVisible("payment-attachment-voided-amount") ? undefined : "none"
                  }} align="right">
												Voided Payment Attachment Amount
											</TableCell>

											<TableCell data-payment-attachment-field="payment-attachment-payment-action" sx={{
                    minWidth: 180,
                    width: 180,
                    display: isFileFormFieldVisible("payment-attachment-payment-action") ? undefined : "none"
                  }}>
												Payment Action
											</TableCell>
											<TableCell data-payment-attachment-field="payment-attachment-remove-action" sx={{
                    minWidth: 180,
                    width: 180,
                    display: isFileFormFieldVisible("payment-attachment-remove-action") ? undefined : "none"
                  }}>
												Delete Action
											</TableCell>

											{/* {isFileFormFieldVisible(
            "payment-attachment-payment-action",
            ) &&
            isFileFormFieldVisible(
            "payment-attachment-remove-action",
            ) ? (
            <>
            	<TableCell
            		data-payment-attachment-field="payment-attachment-payment-action"
            		sx={{
            			minWidth: 180,
            			width: 180,
            		}}
            		colSpan={2}>
            		Action
            	</TableCell>
            </>
            ) : (
            <>
            	<TableCell
            		data-payment-attachment-field="payment-attachment-payment-action"
            		sx={{
            			minWidth: 180,
            			width: 180,
            			display: isFileFormFieldVisible(
            				"payment-attachment-payment-action",
            			)
            				? undefined
            				: "none",
            		}}>
            		Payment Action
            	</TableCell>
            	<TableCell
            		data-payment-attachment-field="payment-attachment-remove-action"
            		sx={{
            			minWidth: 180,
            			width: 180,
            			display: isFileFormFieldVisible(
            				"payment-attachment-remove-action",
            			)
            				? undefined
            				: "none",
            		}}>
            		Delete Action
            	</TableCell>
            </>
            )} */}
										</>)}
								</TableRow>
							</TableHead>
							<TableBody data-sentry-element="TableBody" data-sentry-source-file="view-file-modal.tsx">
								{(file?._paymentVerifications ?? []).filter(a => a.status !== PAYMENT_VERIFICATION_STATUS.VOID).concat((file?._paymentVerifications ?? []).filter(a => a.status === PAYMENT_VERIFICATION_STATUS.VOID)).map((a, i) => {
                const payment = file?._receipt ? file?._receipt.payments.find(p => p.paymentVerificationId && p.paymentVerificationId === a._id) : undefined;
                const payments = file?._receipt ? file?._receipt.payments.filter(p => p.paymentVerificationId && p.paymentVerificationId === a._id) : [];
                return <TableRow key={`payment-payment-attachment-row-${i}`} sx={a.status === PAYMENT_VERIFICATION_STATUS.VOID ? {
                  backgroundColor: "rgba(255, 0, 0, 0.15) !important"
                } : undefined} onClick={() => {
                  if (a.status === PAYMENT_VERIFICATION_STATUS.VOID) {
                    setCurrentPaymentVerification(a as unknown as PaymentVerification);
                    openPaymentDialogWithPaymentVerification();
                    openPaymentDialogWithPaymentVerificationViewMode();
                  }
                }}>
												{sortPaymentAttachmentCells(<>
														<TableCell data-payment-attachment-field="payment-attachment-number" sx={{
                      minWidth: 300,
                      display: isFileFormFieldVisible("payment-attachment-number") ? undefined : "none"
                    }}>
															{`${a.reference.number}`}
														</TableCell>
														<TableCell data-payment-attachment-field="payment-attachment-amount" sx={{
                      minWidth: 220,
                      display: isFileFormFieldVisible("payment-attachment-amount") ? undefined : "none"
                    }} align="right">
															{a.status === PAYMENT_VERIFICATION_STATUS.VOID ? $d(0, a.currency ?? DEFAULT_CURRENCY_CODE) : a.receivedAmount ? $d(a.receivedAmount, a._transaction?._account?.currency ?? DEFAULT_CURRENCY_CODE) : ""}
														</TableCell>
														<TableCell data-payment-attachment-field="payment-attachment-uploaded" sx={{
                      display: isFileFormFieldVisible("payment-attachment-uploaded") ? undefined : "none"
                    }}>
															{$dayjs(a.createdAt).format(DATESTRING_FORMAT)}
														</TableCell>
														<TableCell data-payment-attachment-field="payment-attachment-attachment" sx={{
                      display: isFileFormFieldVisible("payment-attachment-attachment") ? undefined : "none"
                    }}>
															{a.attachment && <Box sx={{
                        display: "flex",
                        gap: 2,
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        p: 1
                      }}>
																	{IMAGE_FILE_EXTENSIONS.includes(a.attachment.extension.toLocaleLowerCase()) && <img src={a.attachment.url} alt={a.attachment.name} style={{
                          height: 40,
                          width: 40,
                          borderRadius: 4
                        }} onClick={() => handleOpenViewPaymentVerificationDialog(a.attachment)} />}
																	{"pdf".includes(a.attachment.extension.toLocaleLowerCase()) && <PdfIcon01 onClick={() => handleOpenViewPaymentVerificationDialog(a.attachment)} />}
																	<Button variant="contained" color="inherit" sx={{
                          mr: 1
                        }} onClick={async () => {
                          const res = await getFileDownloadUrl.mutateAsync({
                            key: a.attachment.url
                          });
                          const response = await fetch(res); // do NOT add headers
                          const blob = await response.blob();
                          const blobUrl = URL.createObjectURL(blob);
                          const b = document.createElement("a");
                          b.href = blobUrl;
                          b.download = ""; // your desired filename
                          b.click();
                          URL.revokeObjectURL(blobUrl);
                        }}>
																		<SvgIcon color="action">
																			<Download />
																		</SvgIcon>
																	</Button>
																</Box>}
														</TableCell>
														{company.isMultiCurrencyEnabled && <Fragment>
																<TableCell data-payment-attachment-field="payment-attachment-payment-amount" sx={{
                        minWidth: 220,
                        display: isFileFormFieldVisible("payment-attachment-payment-amount") ? undefined : "none"
                      }} align="right">
																	{payments.flatMap(payment => payment.payments).map((p, i) => <div key={`t-payment-amount-${i}`}>
																				{$d(p.amount, p.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE)}
																			</div>)}
																</TableCell>
																<TableCell data-payment-attachment-field="payment-attachment-payment-currency" sx={{
                        display: isFileFormFieldVisible("payment-attachment-payment-currency") ? undefined : "none"
                      }}>
																	{payments.flatMap(payment => payment.payments).map((p, i) => <div key={`t-payment-currency-${i}`}>
																				{p.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE}
																			</div>)}
																</TableCell>
																<TableCell data-payment-attachment-field="payment-attachment-exchange-rate" sx={{
                        display: isFileFormFieldVisible("payment-attachment-exchange-rate") ? undefined : "none"
                      }}>
																	{payments.flatMap(payment => payment.payments).map((p, i) => <div key={`t-payment-exchange-${i}`}>
																				{$toOrderDisplay(p.multiCurrencyConfig, file?.multiCurrencyConfig).label}
																			</div>)}
																</TableCell>
																<TableCell data-payment-attachment-field="payment-attachment-order-currency" sx={{
                        display: isFileFormFieldVisible("payment-attachment-order-currency") ? undefined : "none"
                      }}>
																	{file?.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE}
																</TableCell>
															</Fragment>}
														<TableCell data-payment-attachment-field="payment-attachment-order-amount" sx={{
                      minWidth: 220,
                      display: isFileFormFieldVisible("payment-attachment-order-amount") ? undefined : "none"
                    }} align="right">
															{payments.flatMap(payment => payment.payments).map((p, i) => <div key={`t-payment-exchange-${i}`}>
																		{company.isMultiCurrencyEnabled ? $d(bigMath.mul(p.amount, $toOrderDisplay(p.multiCurrencyConfig, file!.multiCurrencyConfig).rate), file?.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE) : $d(p.amount)}
																	</div>)}
														</TableCell>
														<TableCell data-payment-attachment-field="payment-attachment-received-status" sx={{
                      display: isFileFormFieldVisible("payment-attachment-received-status") ? undefined : "none"
                    }}>
															{a.receivedStatus}
														</TableCell>
														<TableCell data-payment-attachment-field="payment-attachment-payment-stage" sx={{
                      display: isFileFormFieldVisible("payment-attachment-payment-stage") ? undefined : "none"
                    }}>
															{a.paymentStage ? PAYMENT_VERIFICATION_UPLOAD_STAGE_MAP[a.paymentStage] : ""}
														</TableCell>
														<TableCell data-payment-attachment-field="payment-attachment-status" sx={{
                      display: isFileFormFieldVisible("payment-attachment-status") ? undefined : "none"
                    }}>
															{a.status}
														</TableCell>
														<TableCell data-payment-attachment-field="payment-attachment-void" sx={{
                      display: isFileFormFieldVisible("payment-attachment-void") ? undefined : "none"
                    }}>
															{a.status === PAYMENT_VERIFICATION_STATUS.VOID ? "Yes" : a.voidedAt ? "Yes" : "No"}
														</TableCell>
														<TableCell data-payment-attachment-field="payment-attachment-voided-amount" sx={{
                      minWidth: 220,
                      display: isFileFormFieldVisible("payment-attachment-voided-amount") ? undefined : "none"
                    }} align="right">
															{a.status === PAYMENT_VERIFICATION_STATUS.VOID ? a.receivedAmount ? $d(a.receivedAmount, a._transaction?._account?.currency ?? DEFAULT_CURRENCY_CODE) : "" : $d(0, a.currency ?? DEFAULT_CURRENCY_CODE)}
														</TableCell>
														<TableCell data-payment-attachment-field="payment-attachment-payment-action" sx={{
                      minWidth: 120,
                      width: 120,
                      display: isFileFormFieldVisible("payment-attachment-payment-action") ? undefined : "none"
                    }}>
															<Button startIcon={<SvgIcon>
																		<PiggyBank01 />
																	</SvgIcon>} onClick={() => {
                        setCurrentPaymentVerification(a as unknown as PaymentVerification);
                        openPaymentDialogWithPaymentVerification();
                      }} disabled={a.status === PAYMENT_VERIFICATION_STATUS.VOID}>
																{payment ? "Edit" : "Take payment"}
															</Button>
														</TableCell>
														<TableCell data-payment-attachment-field="payment-attachment-remove-action" sx={{
                      minWidth: 60,
                      width: 60,
                      display: isFileFormFieldVisible("payment-attachment-remove-action") ? undefined : "none"
                    }}>
															<RemoveIconButton a={a} isDisabled={!!payment || !!a.transactionId || a.status === PAYMENT_VERIFICATION_STATUS.VOID} />
														</TableCell>
													</>)}
											</TableRow>;
              })}
							</TableBody>
						</Table>
					</TableContainer>
				</Grid>
			</Fragment>;
  };
  const OrderCommentSection: FC = () => {
    if (!company?.isFileCommentEnabled) return null;
    return <CustomPaperSection key="order-comments-section" title="Comments" data-sentry-element="CustomPaperSection" data-sentry-component="OrderCommentSection" data-sentry-source-file="view-file-modal.tsx">
				<Fragment key={`order-comments-section-fragment-0`}>
					<Grid xs={12} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<CommentSection isAttachmentEnabled={company?.isFileCommentAttachmentEnabled} data-sentry-element="CommentSection" data-sentry-source-file="view-file-modal.tsx" />
					</Grid>
				</Fragment>
			</CustomPaperSection>;
  };
  const isLoading = isFileLoading || isAgentsLoading || isClientsLoading || isUsersLoading || isCountersLoading;
  if (!file) return null;
  return <Dialog open fullWidth maxWidth={false} scroll="paper" data-sentry-element="Dialog" data-sentry-component="ViewFileModal" data-sentry-source-file="view-file-modal.tsx">
			{updateFileCommentDialog && <UpdateOrderNumberComment />}
			{updateInvoiceCommentDialog && <UpdateInvoiceNumberComment />}
			{isDeleteFileDialogOpen && <DeleteFileDialog handleConfirm={(fileId: string) => handleCancelFileById(fileId)} onClose={closeDeleteFileDialogOpen} />}
			{isCustomFieldsDialogOpen && <CustomFieldsDialog model={CUSTOM_FIELD_MODEL.FILE} onClose={closeCustomFieldsDialog} />}
			{isPaymentDialogOpen && <PaymentDialog onClose={handleClosePaymentDialog} />}
			{currentPaymentVerification && isPaymentDialogWithPaymentVerification && file && <PaymentDialogWithPaymentVerification paymentVerification={currentPaymentVerification} onClose={handleClosePaymentDialogWithPaymentVerification} isViewMode={isPaymentDialogWithPaymentVerificationViewMode} />}

			{isViewPaymentVerificationDialogDialogOpen && <ViewPaymentVerificationDialog attachment={paymentVerification!} onClose={handleCloseViewPaymentVerificationDialog} />}
			{isInvoiceDialogOpen && <InvoicePdfDialog fixDiscount={updateFileInputSummary.fixDiscountAmount} additionalFee={updateFileInputSummary.additionalFeeAmount} onClose={closeInvoiceDialog} />}
			{isOrderSheetDialogOpen && <OrderSheetDialog onClose={async () => {
      const newRouter = router.query;
      delete newRouter.openOrderSheet;
      await router.push({
        query: {
          ...newRouter
        }
      });
      closeOrderSheetDialog();
    }} />}
			<Stack sx={{
      height: "100dvh",
      overflow: "auto"
    }} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
				{isLoading ? <>
						<Stack direction="row" justifyContent="space-between" spacing={1}>
							<Skeleton variant="rounded" height={40} sx={{
            flex: 1
          }} />
							<Skeleton variant="circular" height={40} width={40} />
						</Stack>
						<Box>
							<Skeleton variant="rounded" height={40} />
						</Box>
						<Box>
							<Grid container spacing={3}>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="circular" height={40} width={40} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12}>
									<Skeleton variant="rounded" height={240} sx={{
                flex: 1
              }} />
								</Grid>
							</Grid>
						</Box>
					</> : <>
						{isProcessPathsDialogOpen && <DefineProcessPathsDialog initialValues={till.updateFileInput?.processPaths ?? []} onDone={async values => {
          await updateFile.mutateAsync({
            id: file._id,
            input: {
              processPaths: values
            }
          });
          updateUpdateFileInput({
            processPaths: values as TillFileInput["processPaths"]
          });
          closeProcessPathsDialog();
        }} onCancel={closeProcessPathsDialog} />}

						<Box sx={{
          p: 3,
          top: 0,
          zIndex: 10,
          width: "100%",
          display: "flex",
          position: "sticky",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "background.paper"
        }}>
							<Typography variant="h6" fontWeight={800} gutterBottom>
								Update {company?.fileAlias.singular ?? "File"}
							</Typography>
							<Box display="flex" flexDirection="row" alignItems="center">
								<FileDisplaySettingsButton />
								{!file?.processPaths?.length && <FileStatus prefix="Move to" onChange={handleMoveFileToColumn} isLoading={isFileMoving} options={processOptions} value={file?._process?._id ?? processOptions[0]?.value} />}
								{!!file?.processPaths?.length && <>
										<LoadingButton variant="contained" endIcon={<SvgIcon>
													<ChevronRight />
												</SvgIcon>} disabled={!nextProcessId} loading={isFileMoving} onClick={() =>
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
              handleMoveFileToColumn(nextProcessId!, true)}>
											Move to next process
										</LoadingButton>
									</>}
								<LoadingButton color="success" variant={file?.isProcessed ? "contained" : "outlined"} startIcon={file?.isProcessed ? <SvgIcon>
												<Check />
											</SvgIcon> : null} loading={isFileProcessing} sx={{
              ml: 2
            }} onClick={handleFileProcessedStatusChanged}>
									{file?.isProcessed ? "Processed" : "Mark processed"}
								</LoadingButton>
								<Button startIcon={<SvgIcon>
											<Printer />
										</SvgIcon>} onClick={() => openOrderSheetDialog()}>
									Order Sheet
								</Button>
								<Button startIcon={<SvgIcon>
											<Printer />
										</SvgIcon>} onClick={() => openInvoiceDialog()}>
									{file?._receipt?.isFullyPaid ? "Receipt" : "Invoice"}
								</Button>
								<IconButton size="small" onClick={onClose} aria-label="Close">
									<SvgIcon>
										<XIcon />
									</SvgIcon>
								</IconButton>
							</Box>
						</Box>

						{/* <OrderFormHeaderSection /> */}
						<Grid container spacing={3} px={2}>
							<Grid xs={12}>
								<OrderFormGeneralInfoSection />
							</Grid>
							<Grid xs={12}>
								<OrderFormBasicInfoSection />
							</Grid>
							<Grid xs={12}>
								<OrderProductInfoSection />
							</Grid>
							<Grid xs={12}>
								<OrderPaymentRecordSection />
							</Grid>
							<Grid xs={12}>
								<OrderInvoiceInfoSection />
							</Grid>
							<Grid xs={12}>
								{/* Move here due to fragment component will make dialog
         refresh state has issue */}
								<CustomPaperSection key="order-payment-payment-attachment-section" title="Payment Attachments">
									<OrderPaymentVerificationSection />
									<Grid xs={12}>
										<UploadPaymentAttachmentButton company={company!} prefills={{
                  fileIds: file?._id ? [file._id] : undefined,
                  currency: company?.isMultiCurrencyEnabled ? file?.multiCurrencyConfig?.selectedCurrency : undefined
                }} mode={UPLOAD_PAYMENT_ATTACHMENT_MODE.ACCOUNT_TRANSACTION} lockReferenceFields={true} />
									</Grid>
								</CustomPaperSection>
							</Grid>
							<Grid xs={12}>
								<OrderCommentSection />
							</Grid>
						</Grid>

						<Box sx={{
          px: 2,
          py: 1,
          bottom: 0,
          zIndex: 10,
          width: "100%",
          display: "flex",
          position: "sticky",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "background.paper"
        }}>
							<Box sx={{
            display: "flex",
            width: "50%",
            my: 2
          }} gap={2}>
								<LoadingButton variant="contained" color="error" loading={isFileProcessing} onClick={() => openDeleteFileDialogOpen()}>
									Delete
								</LoadingButton>
							</Box>
							<Box sx={{
            display: "flex",
            flexDirection: "row-reverse",
            width: "50%",
            my: 2
          }} gap={2}>
								<Button variant="contained" onClick={() => {
              setTillIsOpen(true);
            }} startIcon={<SvgIcon>
											<Pencil01 />
										</SvgIcon>}>
									Add Products
								</Button>
								{file?._receipt?.isFullyPaid ? <Chip sx={{
              ".MuiChip-icon": {
                color: "inherit"
              }
            }} color="success" icon={<SvgIcon>
												<PiggyBank01 />
											</SvgIcon>} onClick={() => openPaymentDialog()} label="Paid" /> : <Button variant="contained" startIcon={<SvgIcon>
												<PiggyBank01 />
											</SvgIcon>} onClick={() => openPaymentDialog()}>
										Take payment
									</Button>}
								{isAdjustmentNeeded && <Button variant="contained" startIcon={<SvgIcon>
												<PiggyBank01 />
											</SvgIcon>} onClick={() => openPaymentDialog()}>
										Adjust payment
									</Button>}
							</Box>
						</Box>
					</>}
			</Stack>
		</Dialog>;
};
const RemoveIconButton: FC<{
  a: NonNullable<GetFileByIdResults["_paymentVerifications"]>[number];
  isDisabled: boolean;
}> = ({
  a,
  isDisabled
}) => {
  const deletePaymentVerification = useDeletePaymentVerification();
  const popover = usePopover<HTMLButtonElement>();
  return <Fragment key={a._id}>
			<Button startIcon={<SvgIcon>
						<Clear />
					</SvgIcon>} color="error" disabled={isDisabled} ref={popover.anchorRef} onClick={e => {
      e.preventDefault();
      popover.handleToggle();
    }} data-sentry-element="Button" data-sentry-source-file="view-file-modal.tsx" />
			<Popover key={a._id} anchorOrigin={{
      horizontal: "left",
      vertical: "bottom"
    }} transformOrigin={{
      horizontal: "left",
      vertical: "top"
    }} anchorEl={popover.anchorRef.current} onClose={popover.handleClose} open={popover.open} data-sentry-element="Popover" data-sentry-source-file="view-file-modal.tsx">
				<Stack spacing={3} sx={{
        p: 3
      }} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
					<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
						Are you sure you want to remove payment attachment?
					</Typography>
					<Stack direction="row" spacing={3} justifyContent="flex-end" data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
						<Button color="inherit" onClick={e => {
            e.preventDefault();
            popover.handleClose();
          }} data-sentry-element="Button" data-sentry-source-file="view-file-modal.tsx">
							Cancel
						</Button>
						<Button color="error" variant="contained" onClick={async () => {
            await toast.promise(
            // eslint-disable-next-line no-async-promise-executor
            new Promise(
            // eslint-disable-next-line no-async-promise-executor
            async (resolve, reject) => {
              try {
                if (a._id) {
                  await deletePaymentVerification.mutateAsync({
                    input: {
                      accountTransactionId: a.transactionId,
                      paymentVerificationId: a._id
                    }
                  });
                }
                popover.handleClose();
                resolve(null);
              } catch (e) {
                reject(e instanceof Error ? e : new Error("Something went wrong"));
              }
            }), {
              loading: "Deleting payment verification...",
              success: "Payment verification updated successfully",
              error: "Failed to payment verification"
            });
          }} data-sentry-element="Button" data-sentry-source-file="view-file-modal.tsx">
							Yes
						</Button>
					</Stack>
				</Stack>
			</Popover>
		</Fragment>; // placeholder - actual implementation lives elsewhere in this file
};
const PaymentDialog: FC<{
  onClose: () => void;
}> = ({
  onClose
}) => {
  const {
    query
  } = useRouter();
  const [prevPayments, setPrevPayments] = useState<GetFileByIdResults["_receipt"]>(undefined);
  const {
    data
  } = useGetCurrentCompany();
  const {
    data: file
  } = useGetCurrentFile();
  const payForFile = usePayForFile();
  const updateReceiptPayment = useUpdateReceiptPayment();
  const {
    updateFileInputSummary
  } = useTillSummary();
  // const permissions = usePermissionFilters();
  const form = useFormik<PayForFileMutationVariables>({
    validateOnMount: true,
    initialValues: {
      id: String(query.fileId),
      input: {
        payments: []
      }
    },
    validationSchema: Yup.object({
      id: Yup.string().required("Required"),
      input: Yup.object({
        payments: Yup.array().of(Yup.object({
          payment_type: Yup.string().required("Required"),
          amount: Yup.number().required("Required"),
          bank_received_date: Yup.string(),
          multiCurrencyConfig: Yup.object({
            selectedCurrency: Yup.string().required(),
            currencyConfigs: Yup.array().of(Yup.object({
              code: Yup.string().required("Required"),
              rate: Yup.number().required("Required"),
              isPrimaryCurrency: Yup.boolean().required("Required")
            }).required("Required"))
          }).nullable()
        }).required("Required")).required("Required")
      })
    }),
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {}
  });
  useEffect(() => {
    if (file) return setPrevPayments(file._receipt);
  }, [file]);
  const totalPaid = file?._receipt?.payments ? file._receipt.payments.reduce((acc, cur) => bigMath.add(acc, cur.payments.reduce((a, c) => bigMath.add(a, bigMath.mul(c.amount, $toOrderDisplay(c.multiCurrencyConfig, file.multiCurrencyConfig).rate)), 0)), 0) : 0;
  return <Dialog open fullWidth maxWidth="xl" data-sentry-element="Dialog" data-sentry-component="PaymentDialog" data-sentry-source-file="view-file-modal.tsx">
			<Stack sx={{
      height: "100dvh",
      overflow: "auto"
    }} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
				<AppBar color="inherit" sx={{
        position: "relative"
      }} data-sentry-element="AppBar" data-sentry-source-file="view-file-modal.tsx">
					<Toolbar data-sentry-element="Toolbar" data-sentry-source-file="view-file-modal.tsx">
						<Typography sx={{
            ml: 2,
            flex: 1
          }} variant="h6" component="div" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
							Taking payment
						</Typography>
						<IconButton sx={{
            ml: 1
          }} onClick={onClose} data-sentry-element="IconButton" data-sentry-source-file="view-file-modal.tsx">
							<SvgIcon data-sentry-element="SvgIcon" data-sentry-source-file="view-file-modal.tsx">
								<X data-sentry-element="X" data-sentry-source-file="view-file-modal.tsx" />
							</SvgIcon>
						</IconButton>
					</Toolbar>
				</AppBar>
				<Grid container spacing={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
					<Grid xs={12} md={6} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						{(data?.paymentTypes ?? []).map(pt => <Grid key={pt._id} xs={6} md={4}>
								<Card variant="outlined" sx={[{
              height: "150px",
              cursor: "pointer",
              ":hover": {
                boxShadow: t => t.shadows[8]
              }
            }, form.values.input.payments.some(p => p.payment_type === pt._id) && {
              boxShadow: t => t.shadows[8],
              borderColor: "primary.main",
              backgroundColor: "primary.main",
              color: t => t.palette.primary.contrastText,
              ".MuiCardHeader-subheader": {
                color: t => t.palette.primary.contrastText
              }
            }]} onClick={() => {
              if (!form.values.input.payments.some(p => p.payment_type === pt._id)) {
                void form.setFieldValue("input.payments", [...form.values.input.payments, {
                  payment_type: pt._id,
                  amount: updateFileInputSummary.grossAmount - (file?._receipt?.payments.reduce((acc, cur) => acc + cur.payments.reduce((a, c) => a + c.amount, 0), 0) ?? 0) - form.values.input.payments.reduce((acc, cur) => acc + cur.amount, 0),
                  bank_received_date: undefined,
                  ...(data?.isMultiCurrencyEnabled ? {
                    multiCurrencyConfig: {
                      selectedCurrency: file?.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE,
                      currencyConfigs: data.currencyConfigs ? data.currencyConfigs.map(c => ({
                        code: c.code,
                        rate: c.rate,
                        isPrimaryCurrency: c.isPrimaryCurrency
                      })) : []
                    }
                  } : undefined)
                }]);
              }
            }}>
									<CardHeader title={pt.name} />
									<CardContent />
									<CardActions />
								</Card>
							</Grid>)}
					</Grid>
					<Grid xs={12} md={6} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<Grid xs={12} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Stack spacing={3} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
								<Divider data-sentry-element="Divider" data-sentry-source-file="view-file-modal.tsx">
									<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
										{" "}
										Selected payment types
									</Typography>
								</Divider>
								{form.values.input.payments.map((p, i) => <Grid key={`${p.payment_type}-container-${i}`} spacing={1} container>
										{p.multiCurrencyConfig && <Grid xs={12}>
												<FormControl fullWidth>
													<InputLabel id="select-currency-label">
														Payment Currency
													</InputLabel>
													<Select labelId="select-currency-label" id="select-currency-select" value={p.multiCurrencyConfig.selectedCurrency} label="Payment Currency" onChange={e => {
                      void form.setFieldValue(`input.payments[${i}].multiCurrencyConfig.selectedCurrency`, e.target.value);
                    }}>
														{(data?.currencyConfigs ?? []).map(c => <MenuItem key={c.code} value={c.code}>
																{c.code}
															</MenuItem>)}
													</Select>
												</FormControl>
											</Grid>}

										<Grid xs={12}>
											<Stack key={`${p.payment_type}-selected-${i}`} direction="row" spacing={1}>
												<div>
													<IconButton sx={{
                        mt: 1
                      }} color="error" onClick={() => {
                        void form.setFieldValue(`input.payments`, form.values.input.payments.filter(payment => payment.payment_type !== p.payment_type));
                      }}>
														<Trash01 />
													</IconButton>
												</div>
												<TextField type="number" fullWidth sx={{
                      flex: 1
                    }} label={`${data?.paymentTypes.find(t => t._id === p.payment_type)?.name} (${p.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE})`} {...form.getFieldProps(`input.payments[${i}].amount`)}
                    // @ts-ignore
                    error={!!form.errors.input?.payments?.[i]?.amount}
                    // @ts-ignore
                    helperText={form.errors.input?.payments?.[i]?.amount} />
											</Stack>
										</Grid>

										<Grid xs={12}>
											<DateTimePicker sx={{
                    width: "100%"
                  }} label="Bank Received" value={form.values.input.payments?.[i]?.bank_received_date ? new Date(form.values.input.payments?.[i].bank_received_date as unknown as string) : null} onChange={value => {
                    if (!value) return;
                    void form.setFieldValue(`input.payments[${i}].bank_received_date`, value.toISOString());
                  }} />
										</Grid>

										{p.multiCurrencyConfig && <Grid xs={12}>
												<TextField fullWidth label="Exchange Rate" value={$toOrderDisplay(p.multiCurrencyConfig, file?.multiCurrencyConfig).label} disabled />
											</Grid>}

										{p.multiCurrencyConfig && <Grid xs={12}>
												<TextField fullWidth label={`Paid Amount (${file?.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE})`} value={bigMath.mul(p.amount ? p.amount : 0, $toOrderDisplay(p.multiCurrencyConfig, file?.multiCurrencyConfig).rate)} disabled />
											</Grid>}
									</Grid>)}
							</Stack>
						</Grid>
					</Grid>
					<Grid xs={12} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						{file?._receipt && <Grid xs={12}>
								<Stack spacing={3}>
									<Divider>
										<Typography variant="body2">
											{" "}
											Previous payment types
										</Typography>
									</Divider>
									{prevPayments?.payments.map((payment, idx) => <>
											{payment.payments.map((p, i) => <PaymentRecord key={`previous-payment-${idx}-${i}`} createdAt={$dayjs(payment.paidAt).format(DATETIMESTRING_FORMAT)} payment={p as ReceiptPaymentPayment} file={file} updatePaymentReceivedDate={value => {
                  const updatedPayment = prevPayments;
                  updatedPayment.payments[idx].payments[i].bank_received_date = value.toISOString();
                  setPrevPayments(updatedPayment);
                  if (!file._receipt) return;
                  void updateReceiptPayment.mutateAsync({
                    id: file._receipt._id,
                    input: {
                      paymentIndex: idx,
                      subPaymentIndex: i,
                      payment: {
                        ...updatedPayment.payments[idx].payments[i],
                        bank_received_date: value.toISOString()
                      }
                    }
                  });
                }} updatePaymentType={value => {
                  const updatedPayment = prevPayments;
                  updatedPayment.payments[idx].payments[i].payment_type = value;
                  setPrevPayments(updatedPayment);
                  if (!file._receipt) return;
                  void updateReceiptPayment.mutateAsync({
                    id: file._receipt._id,
                    input: {
                      paymentIndex: idx,
                      subPaymentIndex: i,
                      payment: {
                        ...updatedPayment.payments[idx].payments[i],
                        payment_type: value
                      }
                    }
                  });
                }} updatePaymentAmount={value => {
                  const updatedPayment = prevPayments;
                  updatedPayment.payments[idx].payments[i].amount = value;
                  setPrevPayments(updatedPayment);
                  if (!file._receipt) return;
                  void updateReceiptPayment.mutateAsync({
                    id: file._receipt._id,
                    input: {
                      paymentIndex: idx,
                      subPaymentIndex: i,
                      payment: {
                        ...updatedPayment.payments[idx].payments[i],
                        amount: Number(value)
                      }
                    }
                  });
                }} />)}
										</>)}
								</Stack>
							</Grid>}
					</Grid>

					{file?._paymentVerifications && <Grid xs={12} container spacing={3} p={3}>
							<Grid xs={12}>
								<Divider>
									<Typography variant="body2">Payment Attachments</Typography>
								</Divider>
							</Grid>
							<Grid xs={12}>
								{file?._paymentVerifications.map((p, i) => <PaymentVerificationList key={`payment-payment-attachment-list-${i}`} company={data} paymentVerification={p} />)}
							</Grid>
						</Grid>}

					{file && <OrderDetailTable file={file} />}

					<Grid xs={12} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<Grid xs={12} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Stack spacing={3} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
								<Divider data-sentry-element="Divider" data-sentry-source-file="view-file-modal.tsx">
									<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx"> Payment Received</Typography>
								</Divider>
							</Stack>
						</Grid>
						<Grid alignContent="center" textAlign="right" xs={6} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
								Total Payment Received Amount (
								{data?.isMultiCurrencyEnabled ? file?.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE : ""}
								) :
							</Typography>
						</Grid>
						<Grid xs={6} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
								<TextField type="text" hiddenLabel sx={{
                flex: 1
              }} disabled value={$d(totalPaid, file?.multiCurrencyConfig?.selectedCurrency)} fullWidth data-sentry-element="TextField" data-sentry-source-file="view-file-modal.tsx" />
							</Typography>
						</Grid>
						<Grid alignContent="center" textAlign="right" xs={6} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
								Outstanding Balance Amount (
								{data?.isMultiCurrencyEnabled ? file?.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE : ""}
								) :
							</Typography>
						</Grid>
						<Grid xs={6} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
								<TextField type="text" hiddenLabel disabled value={$d(bigMath.sub(updateFileInputSummary.grossAmount, totalPaid), file?.multiCurrencyConfig?.selectedCurrency)} fullWidth data-sentry-element="TextField" data-sentry-source-file="view-file-modal.tsx" />
							</Typography>
						</Grid>
					</Grid>
				</Grid>

				<Grid xs={12} container spacing={3} py={3} px={1} justifyContent="flex-end" data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
					<LoadingButton variant="contained" startIcon={<SvgIcon>
								<PiggyBank01 />
							</SvgIcon>} disabled={!form.isValid} loading={payForFile.isLoading}
        // sx={{ position: "absolute", bottom: 16, right: 16 }}
        onClick={async () => {
          if (form.values.input.payments.length > 0) {
            await payForFile.mutateAsync(form.values);
          }
          onClose();
        }} data-sentry-element="LoadingButton" data-sentry-source-file="view-file-modal.tsx">
						Save
					</LoadingButton>
				</Grid>
			</Stack>
		</Dialog>;
};
const PaymentDialogWithPaymentVerification: FC<{
  paymentVerification?: PaymentVerification;
  onClose: () => void;
  isViewMode?: boolean;
}> = ({
  paymentVerification,
  onClose,
  isViewMode
}) => {
  const {
    query
  } = useRouter();
  const permissions = usePermissionFilters();
  const {
    data
  } = useGetCurrentCompany();
  const {
    data: file
  } = useGetCurrentFile();
  const payForFile = usePayForFile();
  const updateReceiptPayment = useUpdateReceiptPayment();
  const updatePaymentVerification = useUpdatePaymentVerification();
  const [paymentStage, setPaymentUploadStatus] = useState<Maybe<PAYMENT_VERIFICATION_UPLOAD_STAGE> | undefined>(paymentVerification!.paymentStage);
  const [prevPayments, setPrevPayments] = useState<GetFileByIdResults["_receipt"]>(undefined);
  const {
    value: isRedirectDialog,
    setTrue: openRedirectDialog,
    setFalse: closeRedirectDialog
  } = useBoolean(false);
  const form = useFormik<PayForFileMutationVariables>({
    validateOnMount: true,
    initialValues: {
      id: String(query.fileId),
      input: {
        paymentVerificationId: paymentVerification?._id,
        transactionId: paymentVerification?.transactionId,
        payments: []
      }
    },
    validationSchema: Yup.object({
      id: Yup.string().required("Required"),
      input: Yup.object({
        paymentVerificationId: Yup.string().optional(),
        payments: Yup.array().of(Yup.object({
          payment_type: Yup.string().required("Required"),
          amount: Yup.number().required("Required"),
          bank_received_date: Yup.string(),
          multiCurrencyConfig: Yup.object({
            selectedCurrency: Yup.string().required(),
            currencyConfigs: Yup.array().of(Yup.object({
              code: Yup.string().required("Required"),
              rate: Yup.number().required("Required"),
              isPrimaryCurrency: Yup.boolean().required("Required")
            }).required("Required"))
          }).nullable()
        }).required("Required")).required("Required")
      })
    }),
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {}
  });
  useEffect(() => {
    if (file) return setPrevPayments(file._receipt);
  }, [file]);
  const handleCloseRedirectDialog = () => {
    void closeRedirectDialog();
    void onClose();
  };
  return <>
			{isRedirectDialog && <RedirectDialog handleClose={handleCloseRedirectDialog} />}
			<Dialog open fullWidth maxWidth="xl" data-sentry-element="Dialog" data-sentry-source-file="view-file-modal.tsx">
				<Stack sx={{
        height: "100dvh",
        overflow: "auto"
      }} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
					<AppBar color="inherit" sx={{
          position: "relative"
        }} data-sentry-element="AppBar" data-sentry-source-file="view-file-modal.tsx">
						<Toolbar data-sentry-element="Toolbar" data-sentry-source-file="view-file-modal.tsx">
							<Typography sx={{
              ml: 2,
              flex: 1
            }} variant="h6" component="div" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
								Taking payment
							</Typography>
							<IconButton sx={{
              ml: 1
            }} onClick={onClose} data-sentry-element="IconButton" data-sentry-source-file="view-file-modal.tsx">
								<SvgIcon data-sentry-element="SvgIcon" data-sentry-source-file="view-file-modal.tsx">
									<X data-sentry-element="X" data-sentry-source-file="view-file-modal.tsx" />
								</SvgIcon>
							</IconButton>
						</Toolbar>
					</AppBar>
					<Grid container spacing={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<Grid xs={12} md={12} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							{(data?.paymentTypes ?? []).map(pt => <Grid key={pt._id} xs={6} md={4}>
									<Card variant="outlined" sx={[{
                height: "150px",
                cursor: "pointer",
                ":hover": {
                  boxShadow: t => t.shadows[8]
                }
              }, form.values.input.payments.some(p => p.payment_type === pt._id) && {
                boxShadow: t => t.shadows[8],
                borderColor: "primary.main",
                backgroundColor: "primary.main",
                color: t => t.palette.primary.contrastText,
                ".MuiCardHeader-subheader": {
                  color: t => t.palette.primary.contrastText
                }
              }]} onClick={() => {
                if (isViewMode) return;
                if (!form.values.input.payments.some(p => p.payment_type === pt._id)) {
                  void form.setFieldValue("input.payments", [...form.values.input.payments, {
                    payment_type: pt._id,
                    amount: undefined,
                    bank_received_date: undefined,
                    ...(data?.isMultiCurrencyEnabled ? {
                      multiCurrencyConfig: {
                        selectedCurrency: file?.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE,
                        currencyConfigs: data.currencyConfigs ? data.currencyConfigs.map(c => ({
                          code: c.code,
                          rate: c.rate,
                          isPrimaryCurrency: c.isPrimaryCurrency
                        })) : []
                      }
                    } : undefined)
                  }]);
                }
              }}>
										<CardHeader title={pt.name} />
										<CardContent />
										<CardActions />
									</Card>
								</Grid>)}
						</Grid>
						<Grid xs={12} md={12} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							<Grid xs={12} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
								<Stack spacing={3} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
									<Divider data-sentry-element="Divider" data-sentry-source-file="view-file-modal.tsx">
										<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx">
											{" "}
											Selected payment types
										</Typography>
									</Divider>
									{form.values.input.payments.map((p, i) => <Grid key={`${p.payment_type}-container-${i}`} spacing={1} container>
											{p.multiCurrencyConfig && <Grid xs={4}>
													<FormControl fullWidth>
														<InputLabel id="select-currency-label">
															Payment Currency
														</InputLabel>
														<Select labelId="select-currency-label" id="select-currency-select" value={p.multiCurrencyConfig.selectedCurrency} label="Payment Currency" onChange={e => {
                        void form.setFieldValue(`input.payments[${i}].multiCurrencyConfig.selectedCurrency`, e.target.value);
                      }}>
															{(data?.currencyConfigs ?? []).map(c => <MenuItem key={c.code} value={c.code}>
																	{c.code}
																</MenuItem>)}
														</Select>
													</FormControl>
												</Grid>}

											<Grid xs={p.multiCurrencyConfig ? 4 : 6}>
												<Stack key={`${p.payment_type}-selected-${i}`} direction="row" spacing={1}>
													<div>
														<IconButton sx={{
                          mt: 1
                        }} color="error" disabled={isViewMode} onClick={() => {
                          void form.setFieldValue(`input.payments`, form.values.input.payments.filter(payment => payment.payment_type !== p.payment_type));
                        }}>
															<Trash01 />
														</IconButton>
													</div>
													<TextField type="number" fullWidth sx={{
                        flex: 1
                      }} label={`${data?.paymentTypes.find(t => t._id === p.payment_type)?.name} (${p.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE})`} {...form.getFieldProps(`input.payments[${i}].amount`)} disabled={isViewMode}
                      // @ts-ignore
                      error={!!form.errors.input?.payments?.[i]?.amount}
                      // @ts-ignore
                      helperText={form.errors.input?.payments?.[i].amount} />
												</Stack>
											</Grid>

											<Grid xs={p.multiCurrencyConfig ? 4 : 6}>
												<DateTimePicker sx={{
                      width: "100%"
                    }} label="Bank Received" value={form.values.input.payments?.[i]?.bank_received_date ? new Date(form.values.input.payments?.[i].bank_received_date as unknown as string) : null} onChange={value => {
                      if (!value) return;
                      void form.setFieldValue(`input.payments[${i}].bank_received_date`, value.toISOString());
                    }} disabled={isViewMode} />
											</Grid>

											{p.multiCurrencyConfig && <Grid xs={6}>
													<TextField fullWidth label="Exchange Rate" value={$toOrderDisplay(p.multiCurrencyConfig, file?.multiCurrencyConfig).label} disabled />
												</Grid>}

											{p.multiCurrencyConfig && <Grid xs={6}>
													<TextField fullWidth label={`Paid Amount (${file?.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE})`} value={bigMath.mul(p.amount ? p.amount : 0, $toOrderDisplay(p.multiCurrencyConfig, file?.multiCurrencyConfig).rate)} disabled />
												</Grid>}
										</Grid>)}
								</Stack>
							</Grid>
						</Grid>

						<Grid xs={12} container spacing={3} p={3} data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
							{file?._receipt && <Grid xs={12}>
									<Stack spacing={3}>
										<Divider>
											<Typography variant="body2">
												{" "}
												Previous payment types
											</Typography>
										</Divider>
										{prevPayments?.payments.filter(p => p.paymentVerificationId && p.paymentVerificationId.toString() === paymentVerification?._id.toString()).map((payment, idx) => <>
													{payment.payments.map((p, i) => <PaymentRecord key={`previous-payment-${idx}-${i}`} createdAt={$dayjs(payment.paidAt).format(DATETIMESTRING_FORMAT)} payment={p as ReceiptPaymentPayment} file={file} updatePaymentReceivedDate={isViewMode ? undefined : value => {
                    const updatedPayment = prevPayments;
                    updatedPayment.payments[idx].payments[i].bank_received_date = value.toISOString();
                    setPrevPayments(updatedPayment);
                    if (!file._receipt) return;
                    void updateReceiptPayment.mutateAsync({
                      id: file._receipt._id,
                      input: {
                        paymentIndex: idx,
                        subPaymentIndex: i,
                        payment: {
                          ...updatedPayment.payments[idx].payments[i],
                          bank_received_date: value.toISOString()
                        }
                      }
                    });
                  }} updatePaymentType={!permissions.EDIT_PAYMENT_TYPE || isViewMode ? undefined : value => {
                    const updatedPayment = prevPayments;
                    updatedPayment.payments[idx].payments[i].payment_type = value;
                    setPrevPayments(updatedPayment);
                    if (!file._receipt) return;
                    void updateReceiptPayment.mutateAsync({
                      id: file._receipt._id,
                      input: {
                        paymentIndex: idx,
                        subPaymentIndex: i,
                        payment: {
                          ...updatedPayment.payments[idx].payments[i],
                          payment_type: value
                        }
                      }
                    });
                  }} updatePaymentAmount={!permissions.EDIT_PAYMENT_AMOUNT || isViewMode ? undefined : value => {
                    const updatedPayment = prevPayments;
                    updatedPayment.payments[idx].payments[i].amount = value;
                    setPrevPayments(updatedPayment);
                    if (!file._receipt) return;
                    void updateReceiptPayment.mutateAsync({
                      id: file._receipt._id,
                      input: {
                        paymentIndex: idx,
                        subPaymentIndex: i,
                        payment: {
                          ...updatedPayment.payments[idx].payments[i],
                          amount: Number(value)
                        }
                      }
                    });
                  }} />)}
												</>)}
									</Stack>
								</Grid>}
						</Grid>

						{paymentVerification && <Grid xs={12} pb={8}>
								<Stack spacing={3}>
									<Divider>
										<Typography variant="body2">
											{" "}
											Payment Attachment Details
										</Typography>
									</Divider>
									<Grid key={`payment-payment-attachment-details`} xs={12} container spacing={2} px={2}>
										<Grid md={3} xs={6} display="flex" alignItems="center">
											<TextField label="Payment Attachment #" value={paymentVerification.reference.number} disabled fullWidth />
										</Grid>
										<Grid md={3} xs={6}>
											<DateTimePicker sx={{
                    width: "100%"
                  }} label="Payment Attachment Received Date" value={paymentVerification.receivedDate ? new Date(paymentVerification.receivedDate) : null} disabled={isViewMode} onAccept={async value => {
                    if (!value) return;
                    const receivedDate = value.toISOString();
                    if (!receivedDate) return;
                    await updatePaymentVerification.mutateAsync({
                      input: {
                        ...(paymentVerification.files ? {
                          fileIds: paymentVerification.files
                        } : undefined),
                        paymentVerificationId: paymentVerification._id,
                        receivedDate: receivedDate
                      }
                    });
                  }} />
										</Grid>
										<Grid md={3} xs={6}>
											<TextField type="number" label="Received Amount" defaultValue={paymentVerification.receivedAmount} disabled={isViewMode} onBlur={async e => {
                    if (isNaN(parseFloat(e.target.value))) {
                      return;
                    } else {
                      const receivedAmount = parseFloat(e.target.value);
                      if (!receivedAmount) return;
                      await updatePaymentVerification.mutateAsync({
                        input: {
                          ...(paymentVerification.files ? {
                            fileIds: paymentVerification.files
                          } : undefined),
                          paymentVerificationId: paymentVerification._id,
                          receivedAmount: receivedAmount
                        }
                      });
                    }
                  }} fullWidth />
										</Grid>
										<Grid md={3} xs={6}>
											<Box sx={{
                    display: "flex",
                    flexDirection: "column"
                  }}>
												<FormControl fullWidth>
													<InputLabel id="payment-status-label">
														Payment Status
													</InputLabel>
													<Select labelId="payment-status-label" id="payment-status-select" value={paymentStage} label="Payment Status" disabled={isViewMode} onChange={async e => {
                        const status = e.target.value;
                        if (!status) return;
                        await updatePaymentVerification.mutateAsync({
                          input: {
                            ...(paymentVerification.files ? {
                              fileIds: paymentVerification.files
                            } : undefined),
                            paymentVerificationId: paymentVerification._id,
                            paymentStage: status as PAYMENT_VERIFICATION_UPLOAD_STAGE
                          }
                        });
                        void setPaymentUploadStatus(status as PAYMENT_VERIFICATION_UPLOAD_STAGE);
                      }}>
														<MenuItem key={PAYMENT_VERIFICATION_UPLOAD_STAGE.DEPOSIT_PAYMENT} value={PAYMENT_VERIFICATION_UPLOAD_STAGE.DEPOSIT_PAYMENT}>
															Deposit Payment
														</MenuItem>
														<MenuItem key={PAYMENT_VERIFICATION_UPLOAD_STAGE.PARTIAL_PAYMENT} value={PAYMENT_VERIFICATION_UPLOAD_STAGE.PARTIAL_PAYMENT}>
															Partial Payment
														</MenuItem>
														<MenuItem key={PAYMENT_VERIFICATION_UPLOAD_STAGE.FINAL_PAYMENT} value={PAYMENT_VERIFICATION_UPLOAD_STAGE.FINAL_PAYMENT}>
															Final Payment
														</MenuItem>
													</Select>
												</FormControl>
											</Box>
										</Grid>
									</Grid>
								</Stack>
							</Grid>}

						{file && <OrderDetailTable file={file} />}
					</Grid>

					<Grid xs={12} container spacing={3} py={5} px={1} justifyContent="flex-end" data-sentry-element="Grid" data-sentry-source-file="view-file-modal.tsx">
						<LoadingButton variant="contained" startIcon={<SvgIcon>
									<PiggyBank01 />
								</SvgIcon>} disabled={!form.isValid || isViewMode} loading={payForFile.isLoading} onClick={async () => {
            if (form.values.input.payments.length > 0) {
              await payForFile.mutateAsync(form.values).then(() => {
                toast.success("Take payment successfully.");
                openRedirectDialog();
              }).catch(err => {
                console.log({
                  err
                });
                toast.error("Something went wrong while taking payment.");
              });
            }
          }} data-sentry-element="LoadingButton" data-sentry-source-file="view-file-modal.tsx">
							Save
						</LoadingButton>
					</Grid>
				</Stack>
			</Dialog>
		</>;
};
const PaymentHistoryTable: FC<{
  file: GetFileByIdResults;
  company: GetCurrentCompanyResults;
}> = ({
  file,
  company
}) => {
  const tableDragScroll = useTableDragScroll();
  const {
    file: fileDisplay
  } = useDataDisplay();
  if (!file._receipt) return;
  if (file._receipt.payments.length === 0) return;
  const paymentRecordFieldIds = ["payment-record-created", "payment-record-bank-received", "payment-record-payment-type", "payment-record-attachment-number", "payment-record-payment-amount", "payment-record-payment-currency", "payment-record-exchange-rate", "payment-record-order-currency", "payment-record-order-amount", "payment-record-payment-stage", "payment-record-voided-amount"];
  const paymentRecordSection = fileDisplay.form.sections.find(section => section.name === "Payment Records");
  const paymentRecordRows = (paymentRecordSection?.rows.length ? paymentRecordSection.rows : [{
    sortingIndex: 0,
    fields: paymentRecordFieldIds.map((id, sortingIndex) => ({
      sortingIndex,
      id,
      isVisible: true
    }))
  }]).slice().sort(sortBySortingIndex);
  const paymentRecordFields = paymentRecordRows.flatMap(row => row.fields.filter(field => field.isVisible).slice().sort(sortBySortingIndex));
  const paymentRecordFieldOrder = new Map(paymentRecordFields.map((field, index) => [field.id, index]));
  const sortPaymentRecordCells = (children: ReactNode): ReactNode[] => {
    const flattenCells = (nodes: ReactNode): ReactNode[] => Children.toArray(nodes).flatMap(node => isValidElement<{
      children?: ReactNode;
    }>(node) && node.type === Fragment ? flattenCells(node.props.children) : [node]);
    const getFieldId = (node: ReactNode) => isValidElement<{
      "data-payment-record-field"?: string;
    }>(node) ? node.props["data-payment-record-field"] : undefined;
    const getFieldOrder = (node: ReactNode) => {
      const fieldId = getFieldId(node);
      return fieldId === undefined ? Number.MAX_SAFE_INTEGER : paymentRecordFieldOrder.get(fieldId) ?? Number.MAX_SAFE_INTEGER;
    };
    return flattenCells(children).filter(cell => {
      const fieldId = getFieldId(cell);
      return fieldId !== undefined && paymentRecordFieldOrder.has(fieldId);
    }).sort((a, b) => getFieldOrder(a) - getFieldOrder(b));
  };
  const isColumnVisible = (id: string) => paymentRecordSection?.rows.flatMap(row => row.fields).find(field => field.id === id)?.isVisible ?? true;
  const visibleColumnCount = ["payment-record-created", "payment-record-bank-received", "payment-record-payment-type", "payment-record-attachment-number", "payment-record-payment-amount", ...(company.isMultiCurrencyEnabled ? ["payment-record-payment-currency", "payment-record-exchange-rate", "payment-record-order-currency"] : []), "payment-record-order-amount", "payment-record-payment-stage", "payment-record-voided-amount"].filter(isColumnVisible).length;
  const columnSx = (id: string) => ({
    display: isColumnVisible(id) ? undefined : "none",
    minWidth: id === "payment-record-attachment-number" ? 300 : ["payment-record-payment-amount", "payment-record-order-amount", "payment-record-voided-amount"].includes(id) ? 220 : 180
  });
  const itemsSummary = calculateFile(file as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>);
  const totalPaid = file._receipt.payments ? file._receipt.payments.reduce((acc, cur) => bigMath.add(acc, cur.payments.reduce((a, c) => bigMath.add(a, bigMath.mul(c.amount, $toOrderDisplay(c.multiCurrencyConfig, file.multiCurrencyConfig).rate)), 0)), 0) : 0;
  return <TableContainer component={Paper} ref={tableDragScroll.tableRef} onMouseDown={tableDragScroll.handleMouseDown} onMouseMove={tableDragScroll.handleMouseMove} onMouseUp={tableDragScroll.handleMouseUp} onMouseLeave={tableDragScroll.handleMouseUp} sx={{
    width: "100%",
    overflowX: "auto"
  }} data-sentry-element="TableContainer" data-sentry-component="PaymentHistoryTable" data-sentry-source-file="view-file-modal.tsx">
			<Table sx={theme => ({
      border: `1.5px solid ${theme.palette.divider}`,
      borderCollapse: "collapse",
      "& th, & td": {
        borderRight: `1.5px solid ${theme.palette.divider}`,
        borderBottom: `1.5px solid ${theme.palette.divider}`
      },
      "& th:last-child, & td:last-child": {
        borderRight: "none"
      },
      "& tbody tr:last-child td": {
        borderBottom: "none"
      }
    })} data-sentry-element="Table" data-sentry-source-file="view-file-modal.tsx">
				<TableHead data-sentry-element="TableHead" data-sentry-source-file="view-file-modal.tsx">
					<TableRow data-sentry-element="TableRow" data-sentry-source-file="view-file-modal.tsx">
						{sortPaymentRecordCells(<>
								<TableCell data-payment-record-field="payment-record-created" sx={columnSx("payment-record-created")}>
									Created
								</TableCell>
								<TableCell data-payment-record-field="payment-record-bank-received" sx={columnSx("payment-record-bank-received")}>
									Bank Received
								</TableCell>
								<TableCell data-payment-record-field="payment-record-payment-type" sx={columnSx("payment-record-payment-type")}>
									Payment Type
								</TableCell>
								<TableCell data-payment-record-field="payment-record-attachment-number" sx={columnSx("payment-record-attachment-number")}>
									Payment Attachment #
								</TableCell>
								<TableCell data-payment-record-field="payment-record-payment-amount" sx={columnSx("payment-record-payment-amount")} align="right">
									Payment Amount
								</TableCell>
								{company.isMultiCurrencyEnabled && <>
										<TableCell data-payment-record-field="payment-record-payment-currency" sx={columnSx("payment-record-payment-currency")}>
											Payment Currency
										</TableCell>
										<TableCell data-payment-record-field="payment-record-exchange-rate" sx={columnSx("payment-record-exchange-rate")}>
											Exchange Rate
										</TableCell>
										<TableCell data-payment-record-field="payment-record-order-currency" sx={columnSx("payment-record-order-currency")}>
											Order Currency
										</TableCell>
									</>}
								<TableCell data-payment-record-field="payment-record-order-amount" sx={columnSx("payment-record-order-amount")} align="right">
									Payment Amt in Order Currency
								</TableCell>
								<TableCell data-payment-record-field="payment-record-payment-stage" sx={columnSx("payment-record-payment-stage")}>
									Payment Stage
								</TableCell>
								<TableCell data-payment-record-field="payment-record-voided-amount" sx={columnSx("payment-record-voided-amount")} align="right">
									Voided Payment Amount
								</TableCell>
							</>)}
					</TableRow>
				</TableHead>
				<TableBody data-sentry-element="TableBody" data-sentry-source-file="view-file-modal.tsx">
					{file._receipt.payments.map((payment, idx) => <TableRow key={`receipt-payment-${idx}`}>
							{sortPaymentRecordCells(<>
									<TableCell data-payment-record-field="payment-record-created" sx={columnSx("payment-record-created")}>
										{$dayjs(payment.paidAt).format(DATESTRING_FORMAT)}
									</TableCell>
									<TableCell data-payment-record-field="payment-record-bank-received" sx={columnSx("payment-record-bank-received")}>
										{payment.payments.map((p, i) => <div key={`bank-received-${i}`}>
												{p.bank_received_date ? $dayjs(p.bank_received_date).format(DATESTRING_FORMAT) : ""}
											</div>)}
									</TableCell>
									<TableCell data-payment-record-field="payment-record-payment-type" sx={columnSx("payment-record-payment-type")}>
										{payment.payments.map((p, i) => <div key={`payment-type-${i}`}>
												{company.paymentTypes.find(pt => pt._id.toString() === p.payment_type.toString())?.name ?? "[UNKNOWN TYPE]"}
											</div>)}
									</TableCell>
									<TableCell data-payment-record-field="payment-record-attachment-number" sx={columnSx("payment-record-attachment-number")}>
										{payment.paymentVerificationId && <div key={`payment-verfication-${idx}`}>
												{file._paymentVerifications?.find(pv => pv._id.toString() === payment.paymentVerificationId!.toString())?.reference.number}
											</div>}
									</TableCell>
									<TableCell data-payment-record-field="payment-record-payment-amount" align="right" sx={columnSx("payment-record-payment-amount")}>
										{payment.payments.map((p, i) => <div key={`payment-amt-${p.amount}-${i}`}>
												{$d(p.amount, p.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE)}
											</div>)}
									</TableCell>
									{company.isMultiCurrencyEnabled && <>
											<TableCell data-payment-record-field="payment-record-payment-currency" sx={columnSx("payment-record-payment-currency")}>
												{payment.payments.map((p, i) => <div key={`payment-type-${i}`}>
														{p.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE}
													</div>)}
											</TableCell>
											<TableCell data-payment-record-field="payment-record-exchange-rate" sx={columnSx("payment-record-exchange-rate")}>
												{payment.payments.map((p, i) => <div key={`payment-type-${i}`}>
														{$toOrderDisplay(p.multiCurrencyConfig, file.multiCurrencyConfig).label}
													</div>)}
											</TableCell>
											<TableCell data-payment-record-field="payment-record-order-currency" sx={columnSx("payment-record-order-currency")}>
												{file.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE}
											</TableCell>
										</>}
									<TableCell data-payment-record-field="payment-record-order-amount" sx={columnSx("payment-record-order-amount")} align="right">
										{payment.payments.map((p, i) => <div key={`payment-amount-${i}`}>
												{company.isMultiCurrencyEnabled ? $d(bigMath.mul(p.amount, $toOrderDisplay(p.multiCurrencyConfig, file.multiCurrencyConfig).rate), file.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE) : $d(p.amount)}
											</div>)}
									</TableCell>
									<TableCell data-payment-record-field="payment-record-payment-stage" align="right" sx={columnSx("payment-record-payment-stage")}>
										{payment.voidedAt ? "Void" : "Complete"}
									</TableCell>
									<TableCell data-payment-record-field="payment-record-voided-amount" align="right" sx={columnSx("payment-record-voided-amount")}>
										{payment.payments.map((p, i) => <div key={`payment-amt-${p.amount}-${i}`}>
												{$d(p.voidedAmount ?? 0, p.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE)}
											</div>)}
									</TableCell>
								</>)}
						</TableRow>)}
					<TableRow key="total-payment-received-amount" data-sentry-element="TableRow" data-sentry-source-file="view-file-modal.tsx">
						<TableCell align="right" colSpan={Math.max(visibleColumnCount - 1, 1)} data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx">
							Total Payment Received Amount (
							{company.isMultiCurrencyEnabled ? file.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE : ""}
							) :
						</TableCell>
						<TableCell align="right" sx={{
            minWidth: 220
          }} data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx">
							{$d(totalPaid, file.multiCurrencyConfig?.selectedCurrency)}
						</TableCell>
					</TableRow>
					<TableRow key="total-outstanding-amount" data-sentry-element="TableRow" data-sentry-source-file="view-file-modal.tsx">
						<TableCell align="right" colSpan={Math.max(visibleColumnCount - 1, 1)} data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx">
							Outstanding Balance Amount (
							{company.isMultiCurrencyEnabled ? file.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE : ""}
							) :
						</TableCell>
						<TableCell align="right" sx={{
            minWidth: 220
          }} data-sentry-element="TableCell" data-sentry-source-file="view-file-modal.tsx">
							{$d(bigMath.sub(itemsSummary.grossAmount, totalPaid), file.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE)}
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</TableContainer>;
};
const ViewPaymentVerificationDialog: FC<{
  attachment: PaymentVerification["attachment"];
  onClose: () => void;
}> = ({
  attachment,
  onClose
}) => {
  const {
    url,
    name,
    extension
  } = attachment;
  return <Dialog open fullWidth maxWidth="md" data-sentry-element="Dialog" data-sentry-component="ViewPaymentVerificationDialog" data-sentry-source-file="view-file-modal.tsx">
			<Stack sx={{
      height: "80dvh",
      overflow: "auto"
    }} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
				<IconButton color="inherit" onClick={onClose} data-sentry-element="IconButton" data-sentry-source-file="view-file-modal.tsx">
					<SvgIcon data-sentry-element="SvgIcon" data-sentry-source-file="view-file-modal.tsx">
						<XIcon data-sentry-element="XIcon" data-sentry-source-file="view-file-modal.tsx" />
					</SvgIcon>
				</IconButton>
				{IMAGE_FILE_EXTENSIONS.includes(extension.toLocaleLowerCase()) && <img src={url} alt={name} style={{
        maxWidth: "100%",
        borderRadius: 4
      }} />}
				{"pdf".includes(extension.toLocaleLowerCase()) && <iframe src={url} style={{
        maxWidth: "100%",
        height: "90%",
        borderRadius: 4
      }} />}
			</Stack>
		</Dialog>;
};
const RedirectDialog: FC<{
  handleClose: () => void;
}> = ({
  handleClose
}) => {
  const router = useRouter();
  return <Dialog open fullWidth maxWidth="xs" data-sentry-element="Dialog" data-sentry-component="RedirectDialog" data-sentry-source-file="view-file-modal.tsx">
			<Stack spacing={2} p={5} data-sentry-element="Stack" data-sentry-source-file="view-file-modal.tsx">
				<Box data-sentry-element="Box" data-sentry-source-file="view-file-modal.tsx">
					<Alert data-sentry-element="Alert" data-sentry-source-file="view-file-modal.tsx">
						<b>Take Payment Success.</b> Return back to Finance Payment
						Verification Page?
					</Alert>
					<Typography variant="body1" alignSelf="center" data-sentry-element="Typography" data-sentry-source-file="view-file-modal.tsx"></Typography>
				</Box>
				<Box sx={{
        display: "flex",
        gap: 2
      }} data-sentry-element="Box" data-sentry-source-file="view-file-modal.tsx">
					<LoadingButton variant="contained" color="inherit" sx={{
          flex: 1
        }} onClick={handleClose} data-sentry-element="LoadingButton" data-sentry-source-file="view-file-modal.tsx">
						NO
					</LoadingButton>
					<LoadingButton variant="contained" color="primary" sx={{
          flex: 1
        }} onClick={() => router.back()} data-sentry-element="LoadingButton" data-sentry-source-file="view-file-modal.tsx">
						YES
					</LoadingButton>
				</Box>
			</Stack>
		</Dialog>;
};